import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '@env';
import { _isDev, _log, _warn } from '@shared/aux_helper_environment';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AiApiWrapperService, AIRequest } from 'app/aiApiWrapper/aiApiWrapperService';
import { GenieStatementResponse } from 'app/services/agents/databricks-genie-ai-chat.models';
import { DataBricksAiGenieChatService } from 'app/services/agents/databricks-genie-ai-chat.service';
import { DATABRICKS_OPERATION_TIMEOUT_MS } from 'app/services/agents/databricks-transient-retry';
import { IanTranslateService, PrismaDynamicEnv } from 'core/services/ian-core-singleton.service';
import { GridStackWidget } from 'gridstack';
import { merge, Observable, of, Subject, Subscription, throwError } from 'rxjs';
import { catchError, concatMap, delay, map, switchMap, takeUntil, tap, timeout } from 'rxjs/operators';
import { DashboardStorageProvider } from './dashboard-storage-provider.interface';

const _subKeyVer = 'sv030_';
const _keyVer = (environment.localStorageKeyVerPrefix || '_v3_') + _subKeyVer;
export const _keyCardsLS = _keyVer + 'ai-dashboard-cards';
export const _keyGalleriesLS = _keyVer + 'ai-dashboard-galleries';

// Duración del caché de data de cards: 24 horas
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000;

// Límites de seguridad
const MAX_QUERY_RESULT_ROWS = 1000; // Máximo de filas a procesar
const MAX_COLUMNS_PER_RESULT = 50; // Máximo de columnas por resultado
const MAX_DATA_ITEMS_PER_CARD = 100; // Máximo items en card.data
const MAX_STRING_LENGTH = 5000; // Máximo largo de strings (prevenir memory leak)
const AI_ANALYSIS_TIMEOUT_MS = 30000; // 30s timeout para análisis de IA
// Timeout para ejecución de queries. Se importa de databricks-transient-retry.ts porque los
// presupuestos de reintento de warm-up se derivan de él: si se declarara acá, bajarlo dejaría a los
// reintentos corriendo más allá del timeout y el usuario vería un TimeoutError en vez del mensaje
// de warm-up.
const QUERY_EXECUTION_TIMEOUT_MS = DATABRICKS_OPERATION_TIMEOUT_MS;
const MAX_QUEUE_SIZE = 50; // Máximo de operaciones en queue
const MAX_AI_PROMPT_LENGTH = 2000; // Máximo largo del prompt de IA

export type CardType = 'chart' | 'list' | 'status' | 'kpi' | 'table';
export type ChartType = 'bar' | 'line' | 'pie' | 'horizontal';
export type KpiStatus = 'success' | 'warning' | 'danger' | 'info';
export type KpiTrend = 'up' | 'down' | 'stable';

/**
 * Respuesta del análisis automático de datos por IA
 * Determina el tipo de card, chart y mapeo de datos más apropiado
 */
export interface AICardAnalysis {
  type: CardType;
  chartType?: ChartType;
  dataMapping: QueryDataMapping;
  reasoning: string; // Explicación de la IA sobre su decisión
  suggestedTitle?: string; // Título sugerido por la IA
}

/**
 * Mapeo de columnas del resultado del query a la estructura de data del card
 * Si no se especifica, usa convención: primera columna = label, segunda = value
 */
export interface QueryDataMapping {
  labelColumn?: string; // Ej: "product_name", "region_name"
  valueColumn?: string; // Ej: "sales_amount", "percentage"
  /**
   * Multi-serie: lista de columnas numéricas a usar como series simultáneas
   * para comparar (ej: ["Venta_Total", "Unidades"]).
   * Cuando length >= 2 y el chartType lo permite (bar/line/horizontal), se
   * renderiza el chart con varias series + dual axis automático si las escalas
   * difieren mucho. Si está vacío o ausente, se usa `valueColumn` (single-serie).
   */
  seriesColumns?: string[];
  statusColumn?: string;
  unitColumn?: string;
  colorColumn?: string;
  previousValueColumn?: string; // Para KPIs con comparación
  trendColumn?: string; // 'up' | 'down' | 'stable'
}

/**
 * Dashboard Gallery (Tab/Group de cards)
 * Permite organizar cards en diferentes "pestañas" o "galerías"
 */
export interface DashboardGallery {
  id: string;
  name: string;
  isDeleted?: boolean; // Marca para eliminación lógica
  user?: String;
}

export interface DashboardCard extends GridStackWidget {
  id: string;
  title: string;
  type?: CardType;
  chartType?: ChartType;
  primaryColor?: string;
  galleryId?: string; // ID de la gallery/tab a la que pertenece este card (opcional)
  query?: string; // Query SQL que ejecutará el endpoint
  aiPrompt?: string; // Prompt de IA que generó el query
  dataMapping?: QueryDataMapping; // Mapeo explícito de columnas (opcional)
  user?: String;

  // Data real que se muestra (undefined = loading)
  data?: Array<{
    label: string;
    value: number;
    status?: string | KpiStatus;
    unit?: string;
    previousValue?: number;
    trend?: KpiTrend;
    color?: string;
  }>;
  /**
   * Datos multi-serie (cuando dataMapping.seriesColumns tiene >=2 cols).
   * Cada serie comparte las mismas etiquetas (alineadas por índice).
   * Si está presente, ai-dashboard-chart renderiza multi-serie + dual axis
   * cuando aplica; sino, usa `data` como single-serie (compat).
   */
  multiSeriesData?: Array<{
    name: string;
    data: Array<{ label: string; value: number }>;
  }>;
  /** Dual axis automático calculado por el transformer (ratio escalas >10x). */
  useDualAxis?: boolean;
  /**
   * True cuando el último refresh falló pero el card preserva data previa.
   * El componente puede mostrar un indicador visual (ej: borde amarillo, ícono warning)
   * para que el usuario sepa que los datos no son frescos.
   */
  isStale?: boolean;
  /**
   * Lista completa de columnas del último queryResult ejecutado.
   * Necesaria para que el diálogo de cambiar tipo pueda ofrecer columnas que NO están
   * actualmente en el mapping (ej.: una 2da numérica que el usuario quiera agregar como serie).
   * Se persiste al ejecutar el query — derivada de `Object.keys(queryResult[0])`.
   */
  availableColumns?: string[];
  // Data fake para desarrollo (se renombra a 'data' tras emular carga)
  data_fake?: Array<{
    label: string;
    value: number;
    status?: string | KpiStatus;
    unit?: string;
    previousValue?: number;
    trend?: KpiTrend;
    color?: string;
  }>;
  insights?: string[];
  description?: string;

  // Manejo de errores
  error?: string; // Mensaje de error si la ejecución falló
  errorDetails?: any; // Detalles completos del error para debugging

  // Sistema de caché
  dataExpiresAt?: number; // Timestamp (ms) cuando expira el caché de data
}

/**
 * AI Dashboard Storage Service
 *
 * Current implementation: localStorage
 * Future implementation: REST API
 *
 * This service abstracts the storage layer for dashboard cards.
 * To migrate to API in the future:
 * 1. Inject HttpClient
 * 2. Replace localStorage methods with HTTP calls
 * 3. Return Observables instead of direct values
 * 4. Component usage remains the same (transparent migration)
 */
@Injectable()
export class AiDashboardStorageService implements OnDestroy {
  // ============================================================================
  // REQUEST QUEUE PARA THROTTLING DE LLAMADAS A SERVICIOS EXTERNOS
  // ============================================================================

  /**
   * Subject para cancelar operaciones en curso cuando el servicio se destruye
   */
  private destroy$ = new Subject<void>();

  /**
   * Queue para requests a Databricks Genie (evita rate limiting)
   * Procesa requests secuencialmente con delay entre cada uno
   */
  private databricksQueue$ = new Subject<{
    operation: () => Observable<any>;
    resolver: (value: any) => void;
    rejecter: (error: any) => void;
  }>();

  /**
   * Queue para requests a AI API Wrapper (evita sobrecarga)
   * Procesa requests secuencialmente con delay entre cada uno
   */
  private aiApiQueue$ = new Subject<{
    operation: () => Observable<any>;
    resolver: (value: any) => void;
    rejecter: (error: any) => void;
  }>();

  /**
   * Delay mínimo entre requests a Databricks (en ms)
   * Ajustable según rate limits del servicio
   */
  private readonly DATABRICKS_THROTTLE_MS = 800;

  /**
   * Delay mínimo entre requests a AI API (en ms)
   * Ajustable según capacidad del servicio
   */
  private readonly AI_API_THROTTLE_MS = 500;

  private currentUser: string | null = null;

  // Contadores para prevenir sobrecarga
  private databricksQueueSize = 0;
  private aiApiQueueSize = 0;

  constructor(
    private translate: IanTranslateService,
    private dataBricksService: DataBricksAiGenieChatService,
    private aiApiWrapper: AiApiWrapperService,
    private storageProvider: DashboardStorageProvider,
    private oidcSecurityService: OidcSecurityService,
    private prismaDynamicEnv: PrismaDynamicEnv
  ) {
    // Debug: Verificar que el provider se inyectó correctamente
    // Se configura con: dashboardStorageProviderFactory / brandCustomization.dataBricksGenieConfig.useApiStorage
    if (!this.storageProvider) {
      console.error('[AiDashboardStorageService] ERROR: storageProvider is undefined!');
      throw new Error('DashboardStorageProvider was not injected properly');
    } else {
      _log('[AiDashboardStorageService] Storage provider initialized:', this.storageProvider.constructor.name);
    }

    // Inicializar usuario actual usando método compartido de la clase base
    this.storageProvider
      .initializeCurrentUser(this.prismaDynamicEnv, this.oidcSecurityService, '[AiDashboardStorageService]')
      .subscribe(user => {
        this.currentUser = user;
      });

    // Inicializar queue de Databricks con throttling
    this.databricksQueue$
      .pipe(
        takeUntil(this.destroy$),
        concatMap(request =>
          request.operation().pipe(
            delay(this.DATABRICKS_THROTTLE_MS), // Esperar después de cada request
            tap(result => request.resolver(result)),
            catchError(error => {
              request.rejecter(error);
              return of(null); // Continuar con siguiente request aunque falle
            })
          )
        )
      )
      .subscribe();

    // Inicializar queue de AI API con throttling
    this.aiApiQueue$
      .pipe(
        takeUntil(this.destroy$),
        concatMap(request =>
          request.operation().pipe(
            delay(this.AI_API_THROTTLE_MS), // Esperar después de cada request
            tap(result => request.resolver(result)),
            catchError(error => {
              request.rejecter(error);
              return of(null); // Continuar con siguiente request aunque falle
            })
          )
        )
      )
      .subscribe();
  }

  /**
   * Ejecuta una operación de Databricks en el queue con throttling
   * Evita saturar el servicio con múltiples requests simultáneos
   *
   * @param operation Función que retorna Observable con la operación a ejecutar
   * @returns Observable con el resultado de la operación
   */
  private queueDatabricksRequest<T>(operation: () => Observable<T>): Observable<T> {
    return new Observable<T>(observer => {
      // Validación 1: Verificar límite de queue
      if (this.databricksQueueSize >= MAX_QUEUE_SIZE) {
        observer.error(new Error(`Queue de Lakehouse llena (${MAX_QUEUE_SIZE} operaciones). Espera un momento.`));
        return;
      }

      this.databricksQueueSize++;

      this.databricksQueue$.next({
        operation,
        resolver: (result: T) => {
          this.databricksQueueSize--;
          observer.next(result);
          observer.complete();
        },
        rejecter: (error: any) => {
          this.databricksQueueSize--;
          observer.error(error);
        },
      });
    });
  }

  /**
   * Ejecuta una operación de AI API en el queue con throttling
   * Evita saturar el servicio con múltiples requests simultáneos
   *
   * @param operation Función que retorna Observable con la operación a ejecutar
   * @returns Observable con el resultado de la operación
   */
  private queueAIApiRequest<T>(operation: () => Observable<T>): Observable<T> {
    return new Observable<T>(observer => {
      // Validación 1: Verificar límite de queue
      if (this.aiApiQueueSize >= MAX_QUEUE_SIZE) {
        observer.error(new Error(`Queue de IA llena (${MAX_QUEUE_SIZE} operaciones). Espera un momento.`));
        return;
      }

      this.aiApiQueueSize++;

      this.aiApiQueue$.next({
        operation,
        resolver: (result: T) => {
          this.aiApiQueueSize--;
          observer.next(result);
          observer.complete();
        },
        rejecter: (error: any) => {
          this.aiApiQueueSize--;
          observer.error(error);
        },
      });
    });
  }

  /**
   * Agrega la propiedad 'user' a cada card si no la tiene
   * El user incluye el código del ambiente (ej: 'user@example.com-DEV')
   * Esto asegura que las cards sean únicas por usuario y ambiente
   */
  private ensureUserInCards(cards: DashboardCard[]): DashboardCard[] {
    if (!this.currentUser) {
      _warn('[ensureUserInCards] No hay usuario actual, cards se guardarán sin user');
      return cards;
    }

    return cards.map(card => {
      if (!card.user) {
        _log(`[ensureUserInCards] Agregando user '${this.currentUser}' a card ${card.id}`);
        return { ...card, user: this.currentUser ?? undefined };
      }
      return card;
    });
  }

  /**
   * Agrega la propiedad 'user' a cada gallery si no la tiene
   * El user incluye el código del ambiente (ej: 'user@example.com-DEV')
   * Esto asegura que las galleries sean únicas por usuario y ambiente
   */
  private ensureUserInGalleries(galleries: DashboardGallery[]): DashboardGallery[] {
    if (!this.currentUser) {
      _warn('[ensureUserInGalleries] No hay usuario actual, galleries se guardarán sin user');
      return galleries;
    }

    return galleries.map(gallery => {
      if (!gallery.user) {
        _log(`[ensureUserInGalleries] Agregando user '${this.currentUser}' a gallery ${gallery.id}`);
        return { ...gallery, user: this.currentUser };
      }
      return gallery;
    });
  }

  /**
   * Limpieza al destruir el servicio
   * Cancela todas las operaciones en curso
   */
  ngOnDestroy(): void {
    _log('[AiDashboardStorageService] Destroying service and canceling operations');
    this.destroy$.next();
    this.destroy$.complete();
    this.databricksQueue$.complete();
    this.aiApiQueue$.complete();
  }

  /**
   * Save cards to storage
   *
   * Uses configured storage provider (localStorage or API)
   * Automatically adds 'user' property if not present
   */
  saveCards(cards: DashboardCard[]): Observable<void> {
    const cardsWithUser = this.ensureUserInCards(cards);
    return this.storageProvider.saveCards(cardsWithUser);
  }

  /**
   * Load cards from storage with updated translations
   * Returns default cards if no saved data exists
   *
   * Automatically assigns galleryId to cards:
   * - Cards without galleryId → assigned to first gallery
   * - Cards with invalid galleryId → assigned to first gallery
   *
   * Current: localStorage
   * Future: GET /api/dashboard/cards
   *
   * TODO: For API implementation, replace of() with HTTP call:
   * return this.http.get<DashboardCard[]>('/api/dashboard/cards').pipe(
   *   map(cards => cards.map(card => ({
   *     ...card,
   *     title: this.getTranslatedCardTitle(card.type, card.title),
   *     card_content: this.getTranslatedCardContent(card.type, card.card_content),
   *   }))),
   *   catchError(() => this.getDefaultCards()) // Fallback to defaults on API error
   * );
   */
  loadCards(): Observable<DashboardCard[]> {
    // Primero cargar galerías para validar galleryId de las cards
    return this.loadGalleries().pipe(
      switchMap(galleries => {
        if (galleries.length === 0) {
          _warn('[loadCards] No hay galerías disponibles');
          // Si no hay galerías, devolver cards sin validar galleryId
          return this.loadCardsInternal();
        }

        const validGalleryIds = new Set(galleries.map(g => g.id));
        const defaultGalleryId = galleries[0].id;
        _log(`[loadCards] Galerías cargadas: ${galleries.length}, default galleryId: ${defaultGalleryId}`);

        return this.loadCardsInternal().pipe(
          map(cards => {
            // Asignar galleryId a cards que no lo tienen o tienen uno inválido
            return cards.map(card => {
              if (!card.galleryId || !validGalleryIds.has(card.galleryId)) {
                if (card.galleryId) {
                  _log(`[loadCards] Card ${card.id} tiene galleryId inválido '${card.galleryId}', asignando '${defaultGalleryId}'`);
                } else {
                  _log(`[loadCards] Card ${card.id} sin galleryId, asignando '${defaultGalleryId}'`);
                }

                return {
                  ...card,
                  galleryId: defaultGalleryId,
                };
              }
              return card;
            });
          })
        );
      })
    );
  }

  /**
   * Internal method to load cards from storage without gallery validation
   * Uses configured storage provider (localStorage or API)
   * @private
   */
  private loadCardsInternal(): Observable<DashboardCard[]> {
    return this.storageProvider.loadCards().pipe(
      switchMap(loadedCards => {
        if (loadedCards && loadedCards.length > 0) {
          _log('Cards loaded from storage provider');
          return of(loadedCards);
        }
        // No saved cards, return default cards
        _log('No saved cards found, returning default cards');
        return this.getDefaultCards().pipe(tap(defaultCards => _log('Default cards:', defaultCards)));
      }),
      catchError(error => {
        console.error('Error loading dashboard cards:', error);
        // On error, return default cards
        return this.getDefaultCards();
      })
    );
  }

  /**
   * Get default cards configuration for Latin American retail industry
   * @private Internal use only
   */
  private getDefaultCards(): Observable<DashboardCard[]> {
    const cards = (_isDev() ? _defaultDataCards_DEV : _defaultDataCards_PROD) as DashboardCard[];
    _log(
      '[getDefaultCards] Returning cards:',
      cards.map(c => ({ id: c.id, hasQuery: !!c.query, hasPrompt: !!c.aiPrompt }))
    );
    return of(cards);
  }

  /**
   * Clear all cards from storage
   *
   * Uses configured storage provider (localStorage or API)
   */
  clearAllCards(): Observable<void> {
    return this.storageProvider.clearAllCards();
  }

  /**
   * Get translated card title based on type
   */
  private getTranslatedCardTitle(type: CardType, fallback: string): string {
    switch (type) {
      case 'chart':
        return this.translate.instant('AI_DASHBOARD.CARD_TYPES.CHART');
      case 'kpi':
        return this.translate.instant('AI_DASHBOARD.CARD_TYPES.KPI');
      case 'list':
        return this.translate.instant('AI_DASHBOARD.CARD_TYPES.LIST');
      case 'status':
        return this.translate.instant('AI_DASHBOARD.CARD_TYPES.STATUS');
      default:
        return fallback || this.translate.instant('AI_DASHBOARD.CARD_TYPES.DEFAULT');
    }
  }

  // Colores disponibles para asignación aleatoria
  private readonly CHART_COLORS = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#607D8B', '#FF5722', '#1976D2', '#795548'];

  /**
   * Verifica si el caché de data de un card es válido
   * @param card Card a verificar
   * @returns true si el card tiene data válida y no ha expirado
   */
  private isCacheValid(card: DashboardCard): boolean {
    // Si no tiene data, caché no válido
    if (!card.data || card.data.length === 0) {
      return false;
    }

    // Si no tiene dataExpiresAt, considerar expirado (re-ejecutar)
    if (!card.dataExpiresAt) {
      return false;
    }

    // Verificar si el timestamp de expiración aún es válido
    const now = Date.now();
    const isValid = now < card.dataExpiresAt;

    if (isValid) {
      const remainingMinutes = Math.floor((card.dataExpiresAt - now) / (60 * 1000));
      if (false) _log(`[isCacheValid] Card ${card.id} tiene caché válido (expira en ${remainingMinutes} min)`);
    } else {
      if (false) _log(`[isCacheValid] Card ${card.id} caché expirado, se re-ejecutará`);
    }

    return isValid;
  }

  /**
   * Asigna colores a los items de data de un card según su configuración
   *
   * Estrategia de asignación:
   * 1. Si es un card de 1 solo item con primaryColor → sincronizar color del item con primaryColor
   * 2. Si todos los items ya tienen colores → preservarlos
   * 3. Si faltan colores → asignar aleatoriamente desde CHART_COLORS y actualizar primaryColor
   *
   * @param card Card con la configuración (primaryColor, type, id)
   * @param data Array de items a procesar
   * @returns Object con data procesada y primaryColor actualizado (si corresponde)
   */
  private assignColorsToCardData(
    card: DashboardCard,
    data: Array<{ label: string; value: number; color?: string; [key: string]: any }>
  ): { processedData: typeof data; updatedPrimaryColor?: string } {
    if (data.length === 0) {
      return { processedData: data };
    }

    // CASO 1: Todos los items ya tienen colores asignados
    // → Preservarlos tal cual (MÁXIMA PRIORIDAD)
    if (data.every(item => item.color)) {
      if (false) _log(`Card ${card.id} - data already has colors, preserving them`);
      return { processedData: data };
    }

    // CASO 2: Card con un solo item y primaryColor definido pero sin color propio
    // → Sincronizar color del item con primaryColor del card
    if (card.primaryColor && data.length === 1 && !data[0].color) {
      _log(`Card ${card.id} (${card.type}) - syncing single item color with primaryColor: ${card.primaryColor}`);
      const processedData = data.map(item => ({
        ...item,
        color: card.primaryColor,
      }));
      return { processedData };
    }

    // CASO 3: Faltan colores en algunos/todos los items
    // → Asignar colores aleatorios desde CHART_COLORS
    if (false) _log(`Card ${card.id} - assigning random colors to data items`);
    const startIndex = Math.floor(Math.random() * this.CHART_COLORS.length);

    const processedData = data.map((item, index) => ({
      ...item,
      color: item.color || this.CHART_COLORS[(startIndex + index) % this.CHART_COLORS.length],
    }));

    // Si el card no tiene primaryColor, asignar uno basado en el startIndex
    const updatedPrimaryColor = card.primaryColor ? undefined : this.CHART_COLORS[startIndex];

    return { processedData, updatedPrimaryColor };
  }

  /**
   * Transforma GenieStatementResponse (resultado de Databricks) a array de objetos planos
   *
   * Convierte la estructura compleja data_typed_array a un formato simple:
   * [{ col1: val1, col2: val2 }, ...]
   */
  private transformGenieResponseToRows(genieResponse: GenieStatementResponse): Array<Record<string, any>> {
    if (!genieResponse?.statement_response) {
      _warn('[transformGenieResponseToRows] GenieStatementResponse no tiene statement_response');
      return [];
    }

    if (!genieResponse.statement_response.result?.data_typed_array) {
      _warn('[transformGenieResponseToRows] GenieStatementResponse no tiene data_typed_array');
      return [];
    }

    const { data_typed_array } = genieResponse.statement_response.result;
    const columns = genieResponse.statement_response.manifest?.schema?.columns || [];

    if (columns.length === 0) {
      _warn('[transformGenieResponseToRows] GenieStatementResponse no tiene columnas en el schema - El Datalake devolvió respuesta vacía');
      _log('[transformGenieResponseToRows] Response completo:', genieResponse);
      return [];
    }

    // Validación 1: Límite de columnas
    if (columns.length > MAX_COLUMNS_PER_RESULT) {
      console.warn(`[transformGenieResponseToRows] Demasiadas columnas (${columns.length}), limitando a ${MAX_COLUMNS_PER_RESULT}`);
      columns.length = MAX_COLUMNS_PER_RESULT;
    }

    // Validación 2: Límite de filas
    if (data_typed_array.length > MAX_QUERY_RESULT_ROWS) {
      console.warn(`[transformGenieResponseToRows] Demasiadas filas (${data_typed_array.length}), limitando a ${MAX_QUERY_RESULT_ROWS}`);
      data_typed_array.length = MAX_QUERY_RESULT_ROWS;
    }

    // Transformar cada row a un objeto plano
    return data_typed_array.map(row => {
      const rowObj: Record<string, any> = {};

      // Validación 3: Verificar que row tenga values
      if (!row || !row.values || !Array.isArray(row.values)) {
        console.warn('[transformGenieResponseToRows] Fila inválida detectada, omitiendo');
        return rowObj;
      }

      row.values.forEach((value, index) => {
        if (index >= columns.length) return; // No procesar más allá de las columnas válidas

        const columnName = columns[index]?.name || `col_${index}`;

        // Extraer valor según su tipo
        if (value.str !== undefined) {
          // Validación 4: Truncar strings muy largos
          rowObj[columnName] = value.str.length > MAX_STRING_LENGTH ? value.str.substring(0, MAX_STRING_LENGTH) + '...' : value.str;
        } else if (value.num !== undefined) {
          // Validación 5: Verificar que sea un número válido
          rowObj[columnName] = isFinite(value.num) ? value.num : 0;
        } else if (value.bool !== undefined) {
          rowObj[columnName] = value.bool;
        } else {
          rowObj[columnName] = null;
        }
      });

      return rowObj;
    });
  }

  /**
   * Transforma el resultado del query (array de objetos) al formato DashboardCard.data
   *
   * Estrategia:
   * - Si existe dataMapping, usa los nombres de columna especificados
   * - Si no, usa convención: 1ra columna = label, 2da = value
   * - Asigna colores aleatorios de CHART_COLORS si no hay primaryColor en el card
   *
   * Ejemplo:
   * ```
   * // Resultado del endpoint:
   * [{ product_name: 'Widget A', sales_amount: 12500, status: 'success' }]
   *
   * // Con mapeo explícito:
   * transformQueryResultToData(result, { labelColumn: 'product_name', valueColumn: 'sales_amount' })
   *
   * // Con convención (1ra col = label, 2da = value):
   * transformQueryResultToData(result)
   * ```
   * @private Internal use only
   */
  private transformQueryResultToData(
    queryResult: Array<Record<string, any>>,
    mapping?: QueryDataMapping,
    primaryColor?: string
  ): DashboardCard['data'] {
    if (!queryResult || queryResult.length === 0) {
      return [];
    }

    // Validación 1: Limitar cantidad de items en resultado
    if (queryResult.length > MAX_DATA_ITEMS_PER_CARD) {
      console.warn(`[transformQueryResultToData] Demasiados items (${queryResult.length}), limitando a ${MAX_DATA_ITEMS_PER_CARD}`);
      queryResult = queryResult.slice(0, MAX_DATA_ITEMS_PER_CARD);
    }

    const firstRow = queryResult[0];

    // Validación 2: Verificar que firstRow sea un objeto válido
    if (!firstRow || typeof firstRow !== 'object') {
      console.error('[transformQueryResultToData] Primera fila no es un objeto válido');
      return [];
    }

    const columnNames = Object.keys(firstRow);

    // Determinar columnas (explícitas o por convención)
    const labelCol = mapping?.labelColumn || columnNames[0];
    const rawValueCol = mapping?.valueColumn || columnNames[1];

    // Caso especial: una sola columna sin mapping explícito → el único valor ES el KPI/value.
    // El nombre de columna actúa de label; su celda numérica, de value.
    const usingSingleColumnAsValue = !rawValueCol && columnNames.length === 1;
    let valueCol = usingSingleColumnAsValue ? columnNames[0] : rawValueCol;

    // Guard: si el valueCol resuelto no produce un número válido en la primera fila (ej: mapping
    // guardado con 1 col, ahora hay 2 y la convención primera/segunda col quedó invertida),
    // buscar la primera columna numérica disponible que no sea el labelCol.
    if (!usingSingleColumnAsValue && valueCol && firstRow) {
      const sampleVal = firstRow[valueCol];
      const isRealNumber = (v: any) => v !== null && v !== undefined && v !== '' && typeof v !== 'boolean' && isFinite(Number(v));
      if (!isRealNumber(sampleVal)) {
        const numericFallback = columnNames.find(col => col !== labelCol && isRealNumber(firstRow[col]));
        if (numericFallback) valueCol = numericFallback;
      }
    }

    const statusCol = mapping?.statusColumn;
    const unitCol = mapping?.unitColumn;
    const colorCol = mapping?.colorColumn;
    const previousValueCol = mapping?.previousValueColumn;
    const trendCol = mapping?.trendColumn;

    // Determinar si necesitamos asignar colores aleatorios
    const shouldAssignRandomColors = !primaryColor && !colorCol;

    return queryResult.map((row, index) => {
      // Validación 3: Verificar que row sea válido
      if (!row || typeof row !== 'object') {
        return { label: 'Invalid', value: 0 };
      }

      const dataItem: DashboardCard['data'][0] = {
        // Cuando toda la info es una sola columna, el label es el nombre de esa columna
        // (descriptivo), no el valor numérico de la celda.
        label: usingSingleColumnAsValue ? labelCol : String(row[labelCol] || '').substring(0, 200),
        value: isFinite(Number(row[valueCol])) ? Number(row[valueCol]) : 0,
      };

      // Campos opcionales
      if (statusCol && row[statusCol]) dataItem.status = row[statusCol];
      if (unitCol && row[unitCol]) dataItem.unit = row[unitCol];

      // Asignar color: prioritario desde columna, luego desde primaryColor, finalmente aleatorio
      if (colorCol && row[colorCol]) {
        dataItem.color = row[colorCol];
      } else if (primaryColor) {
        dataItem.color = primaryColor;
      } else if (shouldAssignRandomColors) {
        dataItem.color = this.CHART_COLORS[index % this.CHART_COLORS.length];
      }

      if (previousValueCol && row[previousValueCol] !== undefined) {
        const pv = Number(row[previousValueCol]);
        dataItem.previousValue = isFinite(pv) ? pv : 0;
      }
      if (trendCol && row[trendCol]) dataItem.trend = row[trendCol] as KpiTrend;

      return dataItem;
    });
  }

  /**
   * Transforma un queryResult crudo a multi-serie (varias columnas numéricas vs una de etiqueta).
   *
   * Se invoca cuando `mapping.seriesColumns` tiene >=2 columnas. Devuelve `null` si no aplica
   * (faltan columnas, mapping inválido, queryResult vacío, etc.) y el caller debe seguir con
   * el path single-serie habitual.
   *
   * `useDualAxis` lo decide aquí mismo con la misma heurística que el chart del chat:
   * 2 series + max(abs) ratio >10x → true; sino false.
   */
  private transformQueryResultToMultiSeriesData(
    queryResult: Array<Record<string, any>>,
    mapping: QueryDataMapping
  ): { multiSeriesData: NonNullable<DashboardCard['multiSeriesData']>; useDualAxis: boolean } | null {
    try {
      if (!Array.isArray(queryResult) || queryResult.length === 0) return null;
      if (!mapping?.seriesColumns || mapping.seriesColumns.length < 2) return null;

      const firstRow = queryResult[0];
      if (!firstRow || typeof firstRow !== 'object') return null;

      const columnNames = Object.keys(firstRow);
      const labelCol = mapping.labelColumn || columnNames[0];

      // Filtrar series existentes en el resultado y distintas del label; máximo 2
      const seriesCols = mapping.seriesColumns.filter(c => c && c !== labelCol && columnNames.includes(c)).slice(0, 2);
      if (seriesCols.length < 2) return null;

      // Truncar a MAX_DATA_ITEMS_PER_CARD para evitar charts con miles de puntos
      let rows = queryResult;
      if (rows.length > MAX_DATA_ITEMS_PER_CARD) {
        console.warn(`[transformQueryResultToMultiSeriesData] Demasiados items (${rows.length}), limitando a ${MAX_DATA_ITEMS_PER_CARD}`);
        rows = rows.slice(0, MAX_DATA_ITEMS_PER_CARD);
      }

      const labels = rows.map(row => String(row?.[labelCol] ?? '').substring(0, 200));

      const multiSeriesData = seriesCols.map(col => ({
        name: col,
        data: rows.map((row, idx) => ({
          label: labels[idx],
          value: isFinite(Number(row?.[col])) ? Number(row[col]) : 0,
        })),
      }));

      // useDualAxis: solo cuando son exactamente 2 series con escalas muy distintas
      let useDualAxis = false;
      if (multiSeriesData.length === 2) {
        const maxAbs = (arr: Array<{ value: number }>) => arr.reduce((m, d) => Math.max(m, Math.abs(d.value || 0)), 0);
        const m1 = maxAbs(multiSeriesData[0].data);
        const m2 = maxAbs(multiSeriesData[1].data);
        if (m1 > 0 && m2 > 0) {
          useDualAxis = Math.max(m1, m2) / Math.min(m1, m2) > 10;
        }
      }

      return { multiSeriesData, useDualAxis };
    } catch (e) {
      console.error('[transformQueryResultToMultiSeriesData] threw', e);
      return null;
    }
  }

  /**
   * Helper unificado: aplica `transformQueryResultToData` (single-serie) y, si el mapping tiene
   * `seriesColumns` con >=2 columnas, también `transformQueryResultToMultiSeriesData`.
   *
   * Devuelve un objeto listo para hacer spread sobre el card:
   * `{ data, multiSeriesData?, useDualAxis? }`. Multi-serie en `null` cuando no aplica
   * (un card single-serie no necesita multiSeriesData en el storage).
   */
  /**
   * Construye un card-con-error preservando la última data buena cuando existe.
   * - Si el card tenía `data` o `multiSeriesData` con contenido → marca `isStale: true`
   *   y mantiene visible el chart anterior + adjunta `error`/`errorDetails`.
   * - Si no había data previa → cae al comportamiento original (data: [], sin stale).
   *
   * Permite que un refresh fallido no rompa visualmente cards que estaban funcionando.
   */
  private buildStaleOrErrorCard(card: DashboardCard, errorMessage: string, errorDetails?: any): DashboardCard {
    const hadGoodData = (card.data && card.data.length > 0) || (card.multiSeriesData && card.multiSeriesData.length > 0);
    if (hadGoodData) {
      return {
        ...card,
        // Data / multiSeriesData / useDualAxis / dataMapping / availableColumns se preservan via spread
        error: errorMessage,
        errorDetails,
        isStale: true,
        // dataExpiresAt queda expirado: próximo intento volverá a refrescar
      };
    }
    return {
      ...card,
      data: [],
      error: errorMessage,
      errorDetails,
      isStale: false,
    };
  }

  /**
   * Determina si el mapping del card está funcionalmente válido y reutilizable.
   * Considera válido si tiene al menos UNA columna mapeada (label, value o series).
   * Sirve para decidir si saltar la llamada IA al refrescar — si el mapping ya está
   * sano, basta con re-ejecutar el query y re-transformar con `buildCardDataFields`.
   */
  private hasUsableMapping(mapping?: QueryDataMapping | null, availableColumns?: string[]): boolean {
    if (!mapping || typeof mapping !== 'object') return false;
    const hasLabel = !!mapping.labelColumn;
    const hasValue = !!mapping.valueColumn;
    const hasSeries = Array.isArray(mapping.seriesColumns) && mapping.seriesColumns.length > 0;

    if (!availableColumns || availableColumns.length === 0) {
      return hasLabel || hasValue || hasSeries;
    }

    // Con columnas disponibles, verificar que al menos una columna mapeada exista en el resultado.
    // Evita reusar un mapping obsoleto cuando el query cambió y devolvió columnas distintas.
    const labelOk = hasLabel && availableColumns.includes(mapping.labelColumn!);
    const valueOk = hasValue && availableColumns.includes(mapping.valueColumn!);
    const seriesOk = hasSeries && mapping.seriesColumns!.some(c => availableColumns.includes(c));
    return labelOk || valueOk || seriesOk;
  }

  private buildCardDataFields(
    queryResult: Array<Record<string, any>>,
    mapping?: QueryDataMapping,
    primaryColor?: string
  ): {
    data: DashboardCard['data'];
    multiSeriesData?: DashboardCard['multiSeriesData'];
    useDualAxis?: boolean;
    availableColumns?: string[];
  } {
    try {
      const data = this.transformQueryResultToData(queryResult, mapping, primaryColor);

      // Capturar las columnas crudas del queryResult para que el diálogo de cambiar tipo
      // pueda ofrecer columnas que no están actualmente mapeadas (ej: una 2da numérica).
      const availableColumns =
        Array.isArray(queryResult) && queryResult.length > 0 && queryResult[0] && typeof queryResult[0] === 'object'
          ? Object.keys(queryResult[0])
          : undefined;

      const multi =
        mapping?.seriesColumns && mapping.seriesColumns.length >= 2
          ? this.transformQueryResultToMultiSeriesData(queryResult, mapping)
          : null;

      if (multi) {
        return { data, multiSeriesData: multi.multiSeriesData, useDualAxis: multi.useDualAxis, availableColumns };
      }

      // Borrar explícitamente multiSeriesData/useDualAxis cuando no aplica para que el spread
      // sobre el card existente limpie valores previos (ej: usuario quitó la 2da serie).
      return { data, multiSeriesData: undefined, useDualAxis: undefined, availableColumns };
    } catch (e) {
      console.error('[buildCardDataFields] threw, devolviendo fields vacíos', e);
      return { data: [], multiSeriesData: undefined, useDualAxis: undefined, availableColumns: undefined };
    }
  }

  /**
   * Analiza los resultados de un query usando IA para determinar automáticamente:
   * - type: tipo de card más apropiado (chart, kpi, list, status)
   * - chartType: tipo de gráfico si es chart (bar, line, pie, horizontal)
   * - dataMapping: mapeo de columnas al formato del card
   *
   * @param queryResult Array de objetos con los resultados del query
   * @param existingTitle Título del card (opcional, ayuda al análisis)
   * @param aiPrompt Prompt original del usuario (opcional, ayuda a discriminar type y chartType)
   * @returns Observable con el análisis del card
   * @deprecated Este método debería usarse internamente. Será movido a private en una futura versión.
   */
  analyzeQueryResultsWithAI(
    queryResult: Array<Record<string, any>>,
    existingTitle?: string,
    aiPrompt?: string
  ): Observable<AICardAnalysis> {
    if (!queryResult || queryResult.length === 0) {
      _warn('[analyzeQueryResultsWithAI] No hay resultados para analizar');
      // Retornar análisis por defecto
      return of({
        type: 'list',
        dataMapping: {},
        reasoning: 'Sin datos para analizar',
      });
    }

    // Tomar una muestra de los resultados (primeras 3 filas) para analizar
    const sample = queryResult.slice(0, 3);
    const columnNames = Object.keys(queryResult[0]);

    const prompt = `Analiza estos resultados de una query SQL y determina:

1. El tipo de visualización más apropiado:
   - "kpi": Para métricas individuales con valor numérico (ej: total ventas, margen, etc)
   - "chart": Para múltiples valores que se pueden graficar (tendencias, comparaciones, distribuciones)
   - "list": Para listados de información variada sin patrón gráfico claro
   - "status": Para estados o niveles de algo (ej: stock por tienda, salud de sistemas)

2. Si es "chart", el tipo de gráfico:
   - "line": Para tendencias temporales o series de tiempo
   - "bar": Para comparaciones entre categorías (vertical)
   - "horizontal": Para rankings o comparaciones con muchas categorías
   - "pie": Para distribución de partes de un todo (porcentajes)

3. El mapeo de columnas:
   - labelColumn: columna para etiquetas/nombres
   - valueColumn: columna para valores numéricos principales (serie principal en gráficos de 1 serie)
   - seriesColumns: ARRAY de EXACTAMENTE 2 columnas numéricas para gráficos MULTI-SERIE comparativos
     (ej: comparar "ventas $" vs "unidades vendidas" mes a mes).
     SOLO usar cuando: type="chart", chartType ∈ {bar, line, horizontal}, y los datos
     traen exactamente 2 columnas numéricas COMPARABLES alineadas por la misma etiqueta.
     NUNCA incluir más de 2 columnas en seriesColumns.
     Si lo usás, valueColumn debe ser la primera entrada de seriesColumns.
     Si solo hay 1 columna numérica relevante, OMITIR seriesColumns (single-serie).
   - unitColumn: columna con unidades (si existe)
   - statusColumn: columna con estados (si existe)
   - trendColumn: columna con tendencias up/down/stable (si existe)
   - previousValueColumn: columna con valor anterior para comparación (si existe)

4. Un título descriptivo para el componente (máximo 60 caracteres):
   - Debe ser claro y conciso
   - Debe reflejar el contenido de los datos
   - Basado en la solicitud del usuario si está disponible

Datos de entrada:${aiPrompt ? `\n- Solicitud del usuario: "${aiPrompt}"` : ''}${
      existingTitle ? `\n- Título existente: "${existingTitle}"` : ''
    }
- Columnas disponibles: ${columnNames.join(', ')}
- Muestra de datos (${sample.length} filas):
${JSON.stringify(sample, null, 2)}

Responde ÚNICAMENTE con un JSON válido con esta estructura exacta:
{
  "type": "kpi|chart|list|status",
  "chartType": "line|bar|horizontal|pie" (solo si type es "chart"),
  "suggestedTitle": "Título descriptivo del componente (max 60 caracteres)",
  "dataMapping": {
    "labelColumn": "nombre_columna",
    "valueColumn": "nombre_columna",
    "seriesColumns": ["col_numerica_1", "col_numerica_2"] (OPCIONAL — solo en chart bar/line/horizontal multi-serie),
    "unitColumn": "nombre_columna" (opcional),
    "statusColumn": "nombre_columna" (opcional),
    "trendColumn": "nombre_columna" (opcional),
    "previousValueColumn": "nombre_columna" (opcional)
  },
  "reasoning": "Breve explicación de por qué elegiste este tipo de visualización"
}

IMPORTANTE: Responde SOLO con el JSON, sin texto adicional antes o después.`;

    const aiRequest: AIRequest = {
      prompt,
      maxTokens: 1000,
      temperature: 0.3, // Baja temperatura para respuestas más determinísticas
    };

    _log('[analyzeQueryResultsWithAI] Enviando solicitud a IA:', { columnNames, sampleRows: sample.length });

    // Validación 1: Verificar que el prompt no sea excesivamente largo
    if (prompt.length > MAX_AI_PROMPT_LENGTH * 5) {
      console.warn(`[analyzeQueryResultsWithAI] Prompt muy largo (${prompt.length} chars), esto puede causar timeouts`);
    }

    // Usar queue para evitar saturar el servicio de IA
    return this.queueAIApiRequest(() => this.aiApiWrapper.generateWithBedrockFromAIRequest(aiRequest)).pipe(
      timeout(AI_ANALYSIS_TIMEOUT_MS), // Timeout de 30s
      map(response => {
        _log('[analyzeQueryResultsWithAI] Respuesta de IA:', response.content);

        try {
          // Limpiar respuesta en caso de que venga con markdown o texto extra
          let jsonContent = response.content.trim();

          // Remover bloques de código markdown si existen
          if (jsonContent.startsWith('```')) {
            jsonContent = jsonContent.replace(/```json\n?|```\n?/g, '');
          }

          let analysis = JSON.parse(jsonContent) as AICardAnalysis;

          // Validación 2: Verificar que tenga los campos mínimos requeridos
          if (!analysis.type || !analysis.dataMapping) {
            console.error('[analyzeQueryResultsWithAI] Respuesta de IA inválida, falta type o dataMapping');
            throw new Error('IA no devolvió análisis válido');
          }

          // Validación 3: Verificar que type sea válido
          const validTypes = ['chart', 'kpi', 'list', 'status'];
          if (!validTypes.includes(analysis.type)) {
            console.warn(`[analyzeQueryResultsWithAI] Type '${analysis.type}' no válido, usando 'list'`);
            analysis.type = 'list';
          }

          // Validación 4: Si es chart, verificar chartType
          if (analysis.type === 'chart' && analysis.chartType) {
            const validChartTypes = ['bar', 'line', 'pie', 'horizontal'];
            if (!validChartTypes.includes(analysis.chartType)) {
              console.warn(`[analyzeQueryResultsWithAI] ChartType '${analysis.chartType}' no válido, usando 'bar'`);
              analysis.chartType = 'bar';
            }
          }

          // Validación 5: Truncar suggestedTitle si es muy largo
          if (analysis.suggestedTitle && analysis.suggestedTitle.length > 100) {
            analysis.suggestedTitle = analysis.suggestedTitle.substring(0, 97) + '...';
          }

          // Validación 6: Sanitizar seriesColumns (multi-serie).
          // Requisitos: solo chart, tipos compatibles (bar/line/horizontal), >=2 cols únicas
          // existentes en columnNames y distintas del labelColumn. Sino, descartar.
          if (analysis.dataMapping?.seriesColumns) {
            const compatibleTypes = ['bar', 'line', 'horizontal'];
            const labelCol = analysis.dataMapping.labelColumn;
            const raw = analysis.dataMapping.seriesColumns;

            const isValidShape = analysis.type === 'chart' && analysis.chartType && compatibleTypes.includes(analysis.chartType);

            if (!isValidShape) {
              console.warn('[analyzeQueryResultsWithAI] seriesColumns descartado: type/chartType incompatible', {
                type: analysis.type,
                chartType: analysis.chartType,
              });
              delete analysis.dataMapping.seriesColumns;
            } else if (!Array.isArray(raw)) {
              console.warn('[analyzeQueryResultsWithAI] seriesColumns no es array, descartando');
              delete analysis.dataMapping.seriesColumns;
            } else {
              const cleaned = Array.from(
                new Set(raw.filter(c => typeof c === 'string' && c && c !== labelCol && columnNames.includes(c)))
              ).slice(0, 2); // Máximo 2 series
              if (cleaned.length < 2) {
                console.warn('[analyzeQueryResultsWithAI] seriesColumns con <2 cols válidas, descartando', { raw, cleaned });
                delete analysis.dataMapping.seriesColumns;
              } else {
                analysis.dataMapping.seriesColumns = cleaned;
                // Forzar valueColumn = primera serie (consistencia con single-serie consumers)
                analysis.dataMapping.valueColumn = cleaned[0];
              }
            }
          }

          // Validar que tenga los campos mínimos requeridos
          if (!analysis.type || !analysis.dataMapping) {
            throw new Error('Respuesta de IA inválida: faltan campos requeridos');
          }

          // CORRECCIÓN 1: A veces la IA confunde type con chartType
          // Si type es 'horizontal', 'bar', 'line', o 'pie' → es un chart
          const chartTypes = ['horizontal', 'bar', 'line', 'pie'];
          if (chartTypes.includes(analysis.type as any)) {
            _warn('[analyzeQueryResultsWithAI] IA confundió type con chartType, corrigiendo...');
            analysis = {
              type: 'chart',
              chartType: analysis.type as ChartType,
              dataMapping: analysis.dataMapping,
              reasoning: analysis.reasoning,
            };
            _log('[analyzeQueryResultsWithAI] Análisis corregido:', analysis);
          }

          // CORRECCIÓN 2: Si es gráfico de barras con menos de 10 valores → forzar horizontal
          if (analysis.type === 'chart' && analysis.chartType === 'horizontal' && queryResult.length < 10) {
            _log('[analyzeQueryResultsWithAI] Menos de 10 valores en bar chart, forzando horizontal');
            analysis.chartType = 'bar';
            analysis.reasoning += ' (Forzado a horizontal por tener menos de 10 valores)';
          }

          _log('[analyzeQueryResultsWithAI] Análisis exitoso:', analysis);
          return analysis;
        } catch (error) {
          console.error('[analyzeQueryResultsWithAI] Error parseando respuesta de IA:', error);
          console.error('[analyzeQueryResultsWithAI] Contenido recibido:', response.content);

          // Fallback: análisis por defecto basado en heurísticas simples
          return this.fallbackAnalysis(queryResult, columnNames);
        }
      }),
      catchError(error => {
        console.error('[analyzeQueryResultsWithAI] Error llamando a IA:', error);
        // Fallback: análisis por defecto
        return of(this.fallbackAnalysis(queryResult, columnNames));
      })
    );
  }

  /**
   * Análisis por defecto cuando la IA no está disponible o falla
   * Usa heurísticas simples para determinar el tipo de card
   */
  private fallbackAnalysis(queryResult: Array<Record<string, any>>, columnNames: string[]): AICardAnalysis {
    const rowCount = queryResult.length;

    // Heurística 1: Si hay 1 sola fila → probablemente es un KPI
    if (rowCount === 1) {
      return {
        type: 'kpi',
        dataMapping: {
          labelColumn: columnNames[0],
          valueColumn: columnNames[1] || columnNames[0],
        },
        reasoning: 'Fallback: 1 fila detectada → KPI',
      };
    }

    // Heurística 2: Si hay columnas temporales (fecha, mes, year) → line chart
    const hasTimeColumn = columnNames.some(col => /date|time|mes|month|year|periodo|period/i.test(col));

    if (hasTimeColumn && rowCount > 2) {
      return {
        type: 'chart',
        chartType: 'line',
        dataMapping: {
          labelColumn: columnNames[0],
          valueColumn: columnNames[1] || columnNames[0],
        },
        reasoning: 'Fallback: columna temporal detectada → line chart',
      };
    }

    // Heurística 3: Muchas filas (>10) → horizontal bar para rankings
    if (rowCount > 10) {
      return {
        type: 'chart',
        chartType: 'horizontal',
        dataMapping: {
          labelColumn: columnNames[0],
          valueColumn: columnNames[1] || columnNames[0],
        },
        reasoning: 'Fallback: muchas filas → horizontal bar',
      };
    }

    // Heurística 4: Pocas filas (2-10) → bar chart por defecto
    return {
      type: 'chart',
      chartType: 'bar',
      dataMapping: {
        labelColumn: columnNames[0],
        valueColumn: columnNames[1] || columnNames[0],
      },
      reasoning: 'Fallback: análisis por defecto → bar chart',
    };
  }

  /**
   * Ejecuta una query SQL directamente usando la Statement Execution API de Databricks
   * (Método alternativo más rápido que no usa Genie - sin polling de conversaciones)
   *
   * 1. POST /api/2.0/sql/statements con warehouse_id y query
   * 2. Espera respuesta inline o hace polling del statement_id
   * 3. Transforma data_array a array de objetos planos
   *
   * @param sqlQuery Query SQL a ejecutar
   * @returns Observable con array de objetos [{ col1: val1, col2: val2 }, ...]
   */
  private executeSqlQueryViaStatementAPI(sqlQuery: string): Observable<Array<Record<string, any>>> {
    // Validación 1: Verificar que sqlQuery no esté vacío
    if (!sqlQuery || sqlQuery.trim() === '') {
      return throwError(() => new Error('Query SQL vacío'));
    }

    // Validación 2: Verificar longitud del query
    if (sqlQuery.length > MAX_STRING_LENGTH * 2) {
      console.warn(`[executeSqlQueryViaStatementAPI] Query muy largo (${sqlQuery.length} chars)`);
    }

    _log(`[executeSqlQueryViaStatementAPI] Ejecutando query SQL (método directo):`, sqlQuery);

    // Obtener warehouse_id de la configuración
    const config = this.dataBricksService['getConfig'](); // Acceder al método privado via bracket notation
    const warehouseId = config.WAREHOUSE_ID;

    if (!warehouseId) {
      const errorMsg = 'warehouse_id no está configurado.';
      console.error('[executeSqlQueryViaStatementAPI]', errorMsg);
      return throwError(() => new Error(errorMsg));
    }

    // Usar queue para evitar saturar Databricks con múltiples queries simultáneas
    return this.queueDatabricksRequest(() => this.dataBricksService.executeStatementDirect(sqlQuery, warehouseId)).pipe(
      timeout(QUERY_EXECUTION_TIMEOUT_MS), // Timeout de 60s
      tap(response => _log('[executeSqlQueryViaStatementAPI] Respuesta de Datalake Statement API:', response)),
      map(response => {
        // Verificar estado
        if (response.status.state === 'FAILED') {
          const errorMsg = response.status.error?.message || 'Statement execution failed';
          throw new Error(`Lakehouse error: ${errorMsg}`);
        }

        if (response.status.state !== 'SUCCEEDED') {
          throw new Error(`${this.translate.instant('AI_DASHBOARD.ERRORS.UNEXPECTED_STATEMENT_STATE')}: ${response.status.state}`);
        }

        // Verificar que tenga resultado
        if (!response.result || !response.result.data_array) {
          _warn('[executeSqlQueryViaStatementAPI] No hay data_array en el resultado');
          throw new Error('Lakehouse ejecutó la query pero no devolvió datos estructurados');
        }

        // Obtener columnas del manifest
        const columns = response.manifest?.schema?.columns || [];
        if (columns.length === 0) {
          _warn('[executeSqlQueryViaStatementAPI] No hay columnas en el schema');
          return [];
        }

        // Transformar data_array a array de objetos
        const dataArray = response.result.data_array;
        const rows = dataArray.map(row => {
          const rowObj: Record<string, any> = {};
          columns.forEach((col, index) => {
            rowObj[col.name] = row[index];
          });
          return rowObj;
        });

        _log(`[executeSqlQueryViaStatementAPI] Transformado a ${rows.length} filas`);
        return rows;
      }),
      catchError(error => {
        console.error('[executeSqlQueryViaStatementAPI] Error ejecutando query:', error);
        throw error;
      })
    );
  }

  /**
   * Ejecuta una query SQL directamente usando Databricks Genie AI
   *
   * El flujo es:
   * 1. Envía la query SQL como mensaje a Databricks (iniciando conversación si es necesario)
   * 2. Espera a que Databricks procese y ejecute la query
   * 3. Obtiene el resultado como GenieStatementResponse
   * 4. Transforma el resultado a array de objetos planos
   *
   * @param sqlQuery Query SQL a ejecutar
   * @returns Observable con array de objetos [{ col1: val1, col2: val2 }, ...]
   */
  /**
   * Intenta parsear una tabla markdown del texto que devuelve Genie cuando interpreta el SQL
   * como mensaje conversacional. Formato esperado:
   *   | Col1 | Col2 |
   *   |------|------|
   *   | val  | val  |
   *
   * Devuelve null si no encuentra una tabla válida. Maneja valores formateados comunes:
   * "55.2M" → 55200000, "1.4K" → 1400, "1,444,305" → 1444305.
   *
   * ⚠️ Los valores que Genie muestra pueden estar redondeados; este parser hace su mejor
   * esfuerzo pero los números resultantes pueden ser aproximados. Es un fallback, no
   * una fuente confiable.
   */
  private tryParseMarkdownTable(text: string): Array<Record<string, any>> | null {
    try {
      if (!text || typeof text !== 'string') return null;

      // Encontrar el primer bloque de líneas que parezca tabla markdown
      const lines = text.split(/\r?\n/);
      const tableLineRegex = /^\s*\|.+\|\s*$/;
      const separatorLineRegex = /^\s*\|\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|\s*$/;

      let headerIdx = -1;
      for (let i = 0; i < lines.length - 1; i++) {
        if (tableLineRegex.test(lines[i]) && separatorLineRegex.test(lines[i + 1])) {
          headerIdx = i;
          break;
        }
      }
      if (headerIdx === -1) return null;

      const headerCells = this.splitMarkdownRow(lines[headerIdx]);
      if (headerCells.length < 1) return null;

      // Recolectar filas de datos consecutivas
      const rows: Array<Record<string, any>> = [];
      for (let i = headerIdx + 2; i < lines.length; i++) {
        if (!tableLineRegex.test(lines[i])) break;
        const cells = this.splitMarkdownRow(lines[i]);
        if (cells.length === 0) continue;

        const row: Record<string, any> = {};
        headerCells.forEach((col, idx) => {
          const raw = cells[idx] ?? '';
          row[col] = this.parseMaybeFormattedValue(raw);
        });
        rows.push(row);
      }

      return rows.length > 0 ? rows : null;
    } catch (e) {
      console.warn('[tryParseMarkdownTable] threw, ignorando fallback', e);
      return null;
    }
  }

  private splitMarkdownRow(line: string): string[] {
    return line
      .trim()
      .replace(/^\||\|$/g, '') // quitar pipes inicial/final
      .split('|')
      .map(s => s.trim());
  }

  /**
   * Convierte un valor de celda markdown a number cuando es posible.
   * Soporta sufijos K/M/B (case-insensitive) y separadores de miles (`,`, `.`).
   * Si no parece numérico, devuelve el string original (ej fechas, texto libre).
   */
  private parseMaybeFormattedValue(raw: string): string | number {
    if (raw == null || raw === '') return '';
    const trimmed = raw.trim();

    // Sufijos K / M / B (millones, miles, billones)
    const suffixMatch = trimmed.match(/^(-?[\d.,]+)\s*([KMB])$/i);
    if (suffixMatch) {
      const base = this.parseLocalizedNumber(suffixMatch[1]);
      if (base != null) {
        const mult = { K: 1e3, M: 1e6, B: 1e9 }[suffixMatch[2].toUpperCase() as 'K' | 'M' | 'B'];
        return base * mult;
      }
    }

    // Número plano con separadores de miles
    const asNum = this.parseLocalizedNumber(trimmed);
    if (asNum != null) return asNum;

    // No es número → devolver como string (fechas, categorías)
    return trimmed;
  }

  /**
   * Parsea un string numérico con separadores de miles (`,` o `.`).
   * Devuelve null si no es interpretable como número.
   */
  private parseLocalizedNumber(s: string): number | null {
    if (!s) return null;
    // Asumimos formato en-US (coma = miles, punto = decimal) — es el formato que Genie usa
    // en la mayoría de respuestas en español/inglés observadas.
    const cleaned = s.replace(/,/g, '');
    if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
    const n = Number(cleaned);
    return isFinite(n) ? n : null;
  }

  private executeSqlQueryViaDatabricks(sqlQuery: string): Observable<Array<Record<string, any>>> {
    // Determinar método de ejecución según configuración
    const config = this.dataBricksService['getConfig'](); // Acceder al método privado via bracket notation
    const useStatementAPI = config.USE_STATEMENT_API === true;
    const warehouseId = config.WAREHOUSE_ID;

    // Validar que si quiere usar Statement API, tenga warehouse_id configurado
    if (useStatementAPI && !warehouseId) {
      _warn('[executeSqlQueryViaLakehouse] Statement API habilitado pero warehouse_id no configurado. Haciendo fallback a Genie API.');
      _warn('[executeSqlQueryViaLakehouse] Para usar Statement API, configura warehouse_id');
      // Continuar con Genie (no hacer return aquí)
    } else if (useStatementAPI && warehouseId) {
      _log(`[executeSqlQueryViaLakehouse] Intentando usar Statement API (método directo)`);
      _warn(
        '[executeSqlQueryViaLakehouse] NOTA: LakehouseProxy puede no soportar Statement Execution API. Si falla, se hará fallback automático a Genie API.'
      );
      return this.executeSqlQueryViaStatementAPI(sqlQuery).pipe(
        catchError(error => {
          console.error(
            '[executeSqlQueryViaLakehouse] Statement API falló (posiblemente LakehouseProxy no lo soporta), haciendo fallback a Genie:',
            error
          );
          // Si falla Statement API, intentar con Genie como fallback
          return this.executeWithGenieAPI(sqlQuery);
        })
      );
    }

    // Método tradicional usando Genie
    _log(`[executeSqlQueryViaLakehouse] Usando Genie API (método tradicional con polling)`);
    return this.executeWithGenieAPI(sqlQuery);
  }

  /**
   * Ejecuta query usando Genie API (método tradicional)
   * Extraído como método separado para reutilización en fallback
   */
  private executeWithGenieAPI(sqlQuery: string): Observable<Array<Record<string, any>>> {
    // Validación 1: Verificar que sqlQuery no esté vacío
    if (!sqlQuery || sqlQuery.trim() === '') {
      return throwError(() => new Error('Query SQL vacío'));
    }

    // Validación 2: Verificar longitud del query
    if (sqlQuery.length > MAX_STRING_LENGTH * 2) {
      console.warn(`[executeWithLakehouseProxyApi] Query muy largo (${sqlQuery.length} chars)`);
    }

    _log(`[executeWithLakehouseProxyApi] Ejecutando query SQL:`, sqlQuery);

    // Prefijo de instrucción para Genie: sesga la respuesta hacia ejecución estructurada
    // en vez de conversación. Genie suele "recordar" contexto previo y responder con
    // referencias como "la tabla anterior" — esto reduce esa probabilidad.
    // El header SQL comment es ignorado por el motor SQL pero leído por Genie como hint.
    // El timestamp hace única la cadena para evitar cache conversacional.
    const wrappedQuery =
      `-- Execute this SQL query directly and return only the structured result data. ` +
      `Do NOT respond conversationally or reference previous data. Run query #${Date.now()}.\n` +
      sqlQuery;

    // Usar queue para evitar rate limiting de Databricks Genie
    return this.queueDatabricksRequest(() => this.dataBricksService.sendGenieMessage(wrappedQuery)).pipe(
      timeout(QUERY_EXECUTION_TIMEOUT_MS), // Timeout de 60s
      tap(response => _log('[executeWithLakehouseProxyApi] Respuesta de Lakehouse:', response)),
      map((response: any) => {
        // PRIMERO: Verificar si sendGenieMessage devolvió un error (viene en response.error del tipo GenieErrorType)
        if (response && response.error) {
          // El error viene del catchError de sendGenieMessage, necesitamos extraer la info HTTP
          const errorMsg = response.text || 'Error processing request';
          const enhancedError: any = new Error(errorMsg);

          // Extraer info HTTP que viene directamente en el response
          if (response.status !== undefined) {
            enhancedError.status = response.status;
            enhancedError.statusText = response.statusText;
            enhancedError.url = response.url;
          }
          if (response.method) enhancedError.method = response.method;
          if (response.relativePath) enhancedError.relativePath = response.relativePath;
          if (response.serverResponse) enhancedError.serverResponse = response.serverResponse;

          throw enhancedError;
        }

        // Mejor manejo de errores Genie/Databricks
        if (response && (response.errorMessage || response.statusCode === 502 || response.statusCode === 429)) {
          let errorMsg = '';

          if (response.errorMessage) {
            errorMsg = response.errorMessage;
          } else if (response.error) {
            errorMsg = response.error;
          } else if (response.statusCode === 502) {
            errorMsg = 'Error 502: El servicio de Datalake no está disponible o hubo un problema de red.';
          } else if (response.statusCode === 429) {
            errorMsg = 'Error 429: Demasiadas solicitudes. Por favor, espera un momento y vuelve a intentarlo.';
          } else {
            errorMsg = this.translate.instant('AI_DASHBOARD.ERRORS.UNKNOWN_DATABRICKS_ERROR');
          }

          // Si el error contiene JSON con error_code, parsear y mostrar sugerencia
          let userMessage = `❌ Error al comunicarse con Lakehouse:\n\n${errorMsg}`;

          if (errorMsg.includes('RESOURCE_EXHAUSTED') || errorMsg.includes('TooManyRequests')) {
            userMessage +=
              '\n\nEl servicio recibió demasiadas solicitudes.\n Espera unos segundos y vuelve a intentarlo. Si el problema persiste, reduce la frecuencia de consultas o contacta soporte.';
          }
          throw new Error(userMessage);
        }

        // Verificar si hay resultado de query
        if (!response.queryProcessResult) {
          // Caso especial: Si no hay queryProcessResult, significa que Databricks devolvió el resultado en texto
          // Esto puede pasar por:
          // 1. Genie no pudo ejecutar la query (tablas inexistentes, permisos, sintaxis, etc)
          // 2. Genie interpretó el SQL como una pregunta y respondió en lenguaje natural

          _warn('[executeWithLakehouseProxyApi] No se recibió queryProcessResult. Response:', response);

          // Si hay texto de respuesta, primero intentar parsear una tabla markdown embebida
          // (caso frecuente: Genie interpreta el SQL como pregunta y responde conversacional
          //  con la tabla incluida en el texto). Si lo logramos, devolvemos esas filas.
          // ⚠️ Los valores parseados pueden ser APROXIMADOS — Genie suele formatear (ej "55.2M",
          // "1,444,305"). El usuario puede recargar para intentar obtener datos estructurados exactos.
          if (response.text && response.text.trim()) {
            _log('[executeWithLakehouseProxyApi] Datalake respondió con texto:', response.text);

            const parsedRows = this.tryParseMarkdownTable(response.text);
            if (parsedRows && parsedRows.length > 0) {
              _warn(
                `[executeWithLakehouseProxyApi] Fallback: tabla markdown parseada (${parsedRows.length} filas). ` +
                  'Los valores pueden ser aproximados — recargar para reintentar ejecución estructurada.'
              );
              return parsedRows;
            }

            // Truncar mensaje si es muy largo. La respuesta texto sin tabla puede ser:
            //  (a) Genie respondió conversacionalmente (referenciando contexto de sesión).
            //  (b) Genie no pudo ejecutar (sintaxis, permisos, tablas inexistentes).
            // Distinguimos a vista de pájaro: si el texto contiene marcadores de chat
            // (preguntas, "anterior", "puedes ver", emojis), es (a).
            const errorMsg = response.text.length > 300 ? response.text.substring(0, 300) + '...' : response.text;
            const looksConversational = /\?\s*$|tabla anterior|datos anteriores|previa(mente)?|puedes ver|te gustaría|te interesa/i.test(
              response.text
            );

            const hint = looksConversational
              ? 'Genie respondió de forma conversacional en vez de ejecutar el SQL (puede pasar cuando la sesión retiene contexto previo). Reintentá recargar — normalmente la 2da vez sí ejecuta.'
              : 'Verifica que las tablas/columnas existan y tengas los permisos necesarios.';

            throw new Error(`${errorMsg}\n\nSugerencia: ${hint}`);
          }

          // Sin texto de respuesta, error genérico con más contexto
          throw new Error(
            'El Datalake no devolvió datos estructurados.\n\nPosibles causas:\n• La query tiene errores de sintaxis SQL\n• Las tablas o columnas no existen\n• No tienes permisos para acceder a los datos\n\nIntenta simplificar el prompt o edita el query SQL directamente.'
          );
        } // Transformar GenieStatementResponse a array de objetos
        const rows = this.transformGenieResponseToRows(response.queryProcessResult);

        // Si rows está vacío, significa que el schema estaba vacío o no había datos
        if (rows.length === 0) {
          _warn('[executeWithLakehouseProxyApi] No se pudieron extraer filas del resultado');
          throw new Error(
            'Lakehouse ejecutó la query pero no devolvió datos estructurados. Los resultados solo están disponibles en formato texto en el chat.'
          );
        }

        _log(`[executeWithLakehouseProxyApi] Transformado a ${rows.length} filas`);

        return rows;
      }),
      catchError(error => {
        console.error('[executeWithLakehouseProxyApi] Error ejecutando query:', error);

        // Preservar información HTTP si existe en el error original
        const enhancedError: any = error instanceof Error ? error : new Error(error?.message || 'Unknown error');

        // Si el error tiene propiedades HTTP (viene de genericHandleError), preservarlas
        if (error?.status !== undefined) {
          enhancedError.status = error.status;
          enhancedError.statusText = error.statusText;
          enhancedError.url = error.url;
        }

        throw enhancedError; // Re-throw para que executeCardQuery lo maneje
      })
    );
  }

  /**
   * Ejecuta el query de un card individual y transforma el resultado a data
   *
   * Estrategia:
   * - Si tiene caché válido: devuelve card con data cacheada (no re-ejecuta)
   * - Si solo tiene aiPrompt (sin query): genera query desde prompt y continúa flujo
   * - Si NO tiene data_fake: ejecuta query real usando Databricks API
   * - Si NO tiene metadata (type, chartType, dataMapping): usa IA para determinarla automáticamente
   * - Si tiene data_fake: emula carga y usa data_fake (para desarrollo)
   * - En caso de error: marca card con error pero NO bloquea otras cards
   *
   * @param card Card con query a ejecutar
   * @returns Observable con el card populado con data del query o error
   * @private Internal use only
   */
  private executeCardQuery(card: DashboardCard): Observable<DashboardCard> {
    // CASO ESPECIAL: Card solo con aiPrompt (sin query) → generar query automáticamente
    if (!card.query && card.aiPrompt) {
      _log(`[executeCardQuery] Card ${card.id} solo tiene prompt, generando query automáticamente...`);

      return this.regenerateQueryFromPrompt(card.aiPrompt, card.title).pipe(
        switchMap(
          ({ query: generatedQuery, data: queryData, analysis, transformedData, multiSeriesData, useDualAxis, availableColumns }) => {
            _log(`[executeCardQuery] Query generado para card ${card.id}:`, generatedQuery);
            _log(`[executeCardQuery] Datos obtenidos (${queryData?.length || 0} filas)`);

            // VALIDACIÓN: Si no se generó una consulta válida, devolver error inmediatamente
            if (!generatedQuery || generatedQuery.trim().length === 0) {
              _warn(`[executeCardQuery] No se pudo generar consulta para card ${card.id}`);
              const errorCard = {
                ...card,
                data: [], // Data vacía indica error (no loading)
                error: this.translate.instant('AI_DASHBOARD.ERRORS.QUERY_GENERATION_FAILED'),
                errorDetails: {
                  message: 'Query generation failed',
                  timestamp: new Date().toISOString(),
                  aiPrompt: card.aiPrompt,
                  generatedQuery: null,
                },
                description: this.translate.instant('AI_DASHBOARD.ERRORS.QUERY_GENERATION_FAILED_SHORT'),
              };
              _warn(`[executeCardQuery] Emitiendo card con error:`, errorCard);
              return of(errorCard);
            }

            // Si ya viene con análisis y datos transformados, usar esos directamente
            if (analysis && transformedData) {
              _log(`[executeCardQuery] Usando análisis y datos transformados de regenerateQueryFromPrompt`);

              const updatedCard: DashboardCard = {
                ...card,
                query: generatedQuery,
                type: analysis.type,
                chartType: analysis.chartType,
                dataMapping: analysis.dataMapping,
                description: analysis.reasoning,
                data: transformedData,
                // Propagar campos multi-serie + availableColumns para el diálogo de cambio de tipo
                multiSeriesData,
                useDualAxis,
                availableColumns,
                dataExpiresAt: Date.now() + CACHE_DURATION_MS,
              };

              // Guardar card con todo
              this.updateCardMetadataInStorage(updatedCard);

              return of(updatedCard);
            }

            // Fallback: caso antiguo sin análisis automático
            const updatedCard: DashboardCard = {
              ...card,
              query: generatedQuery,
              type: undefined,
              chartType: undefined,
              dataMapping: undefined,
              description: undefined,
            };

            this.updateCardMetadataInStorage(updatedCard);

            if (queryData && queryData.length > 0) {
              _log(`[executeCardQuery] Datos disponibles pero sin análisis - ejecutando análisis manual`);

              return this.analyzeQueryResultsWithAI(queryData, updatedCard.title, updatedCard.aiPrompt).pipe(
                map(manualAnalysis => {
                  const fields = this.buildCardDataFields(queryData, manualAnalysis.dataMapping, updatedCard.primaryColor);

                  return {
                    ...updatedCard,
                    ...fields,
                    type: manualAnalysis.type,
                    chartType: manualAnalysis.chartType,
                    dataMapping: manualAnalysis.dataMapping,
                    description: manualAnalysis.reasoning,
                    dataExpiresAt: Date.now() + CACHE_DURATION_MS,
                  };
                }),
                tap(finalCard => this.updateCardMetadataInStorage(finalCard))
              );
            }

            // ⚠️ NO hay datos disponibles desde la generación del prompt
            // Esto normalmente NO debería pasar, pero si ocurre, significa que Genie
            // generó el SQL pero no lo ejecutó automáticamente
            _warn(`[executeCardQuery] No hay datos disponibles tras generar query desde prompt para card ${card.id}`);
            _warn(`[executeCardQuery] Esto puede pasar si Genie no ejecutó automáticamente el SQL generado`);

            // ❌ NO intentar re-ejecutar con executeCardQuery porque causaría loop
            // En su lugar, intentar ejecutar directamente con Statement API
            return this.executeSqlQueryViaDatabricks(generatedQuery).pipe(
              switchMap((rows: any[]) => {
                const needsMetadataAnalysis = !updatedCard.dataMapping || Object.keys(updatedCard.dataMapping).length === 0;

                if (needsMetadataAnalysis) {
                  return this.analyzeQueryResultsWithAI(rows, updatedCard.title, updatedCard.aiPrompt).pipe(
                    map(analysis => {
                      const fields = this.buildCardDataFields(rows, analysis.dataMapping, updatedCard.primaryColor);

                      return {
                        ...updatedCard,
                        ...fields,
                        type: analysis.type,
                        chartType: analysis.chartType,
                        dataMapping: analysis.dataMapping,
                        description: analysis.reasoning,
                        dataExpiresAt: Date.now() + CACHE_DURATION_MS,
                      };
                    }),
                    tap(finalCard => this.updateCardMetadataInStorage(finalCard))
                  );
                } else {
                  // Card ya tiene metadata, solo transformar datos
                  const fields = this.buildCardDataFields(rows, updatedCard.dataMapping, updatedCard.primaryColor);

                  return of({
                    ...updatedCard,
                    ...fields,
                    dataExpiresAt: Date.now() + CACHE_DURATION_MS,
                  });
                }
              }),
              catchError(error => {
                console.error(`[executeCardQuery] Error ejecutando SQL generado:`, error);

                const errorDetails: any = {
                  message: error.message || error.text,
                  timestamp: new Date().toISOString(),
                  aiPrompt: card.aiPrompt,
                  sqlQuery: generatedQuery,
                };

                if (error.status !== undefined) {
                  errorDetails.httpStatus = error.status;
                  errorDetails.httpStatusText = error.statusText || 'Unknown Error';
                }
                if (error.url) errorDetails.requestUrl = error.url;
                if (error.method) errorDetails.httpMethod = error.method;
                if (error.relativePath) errorDetails.relativePath = error.relativePath;
                if (error.error) errorDetails.serverError = error.error;
                if (error.serverResponse) errorDetails.serverResponse = error.serverResponse;

                return of({
                  ...updatedCard,
                  data: [],
                  error: `${this.translate.instant('AI_DASHBOARD.ERRORS.EXECUTING_QUERY')}: ${error.message}`,
                  errorDetails,
                });
              })
            );
          }
        ),
        catchError(error => {
          console.error(`[executeCardQuery] Error generando query para card ${card.id}:`, error);

          // Capturar información adicional del error
          const errorDetails: any = {
            message: error.message || this.translate.instant('AI_DASHBOARD.ERRORS.UNKNOWN_ERROR'),
            timestamp: new Date().toISOString(),
            aiPrompt: card.aiPrompt, // Incluir el prompt que falló
          };

          // Si el error viene de un HttpErrorResponse, capturar status y otros detalles
          if (error.status !== undefined) {
            errorDetails.httpStatus = error.status;
            errorDetails.httpStatusText = error.statusText;
          }
          if (error.url) {
            errorDetails.requestUrl = error.url;
          }
          if (error.method) {
            errorDetails.httpMethod = error.method;
          }
          if (error.relativePath) {
            errorDetails.relativePath = error.relativePath;
          }
          if (error.error) {
            errorDetails.serverError = error.error;
          }

          // Devolver card con error
          return of({
            ...card,
            data: [],
            error: `${this.translate.instant('AI_DASHBOARD.ERRORS.GENERATING_QUERY')}: ${
              error.message || this.translate.instant('AI_DASHBOARD.ERRORS.UNKNOWN_ERROR')
            }`,
            errorDetails, // Incluir detalles completos del error
            description: `Error: ${error.message || this.translate.instant('AI_DASHBOARD.ERRORS.UNKNOWN_ERROR')}`,
          });
        })
      );
    }

    // Si no tiene query ni prompt, devolver card tal cual
    if (!card.query) {
      return of(card);
    }

    // VERIFICAR CACHÉ: Si tiene data válida y no ha expirado, devolverla sin re-ejecutar
    if (this.isCacheValid(card)) {
      _log(`[executeCardQuery] Card ${card.id} usando data cacheada`);
      return of(card);
    }

    // CASO 1: Card sin data_fake → ejecutar query real usando Databricks
    if (!card.data_fake || card.data_fake.length === 0) {
      _log(`[executeCardQuery] Card ${card.id} sin data_fake - ejecutando query real via Lakehouse`);

      // Verificar si Databricks está disponible
      if (!this.dataBricksService.CAN_USE_DB_CHAT()) {
        _warn(`[executeCardQuery] El Datalake no está disponible para card ${card.id}`);
        return of(this.buildStaleOrErrorCard(card, 'Servicio de Datalake no disponible'));
      }

      return this.executeSqlQueryViaDatabricks(card.query).pipe(
        switchMap(queryResult => {
          _log(`[executeCardQuery] Query ejecutado exitosamente para card ${card.id}`, queryResult);

          // Verificar si el card necesita análisis automático de metadata.
          // Se considera que el mapping está "funcionalmente válido" si tiene al menos
          // una de estas columnas: labelColumn, valueColumn o seriesColumns. Sino, el
          // mapping está vacío/malformado y conviene re-analizar con IA.
          const resultColumns =
            queryResult.length > 0 && queryResult[0] && typeof queryResult[0] === 'object' ? Object.keys(queryResult[0]) : [];
          const needsMetadataAnalysis = !this.hasUsableMapping(card.dataMapping, resultColumns);

          if (needsMetadataAnalysis) {
            _log(`[executeCardQuery] Card ${card.id} necesita análisis IA (mapping ausente/malformado):`, card.dataMapping);

            // Analizar resultados con IA para determinar type, chartType y dataMapping
            return this.analyzeQueryResultsWithAI(queryResult, card.title, card.aiPrompt).pipe(
              map(analysis => {
                _log(`[executeCardQuery] Análisis IA completado para card ${card.id}:`, analysis);

                // Actualizar metadata del card con el análisis
                const updatedCard = {
                  ...card,
                  type: analysis.type,
                  chartType: analysis.chartType,
                  dataMapping: analysis.dataMapping,
                  description: analysis.reasoning,
                };

                // Transformar resultado: single-serie + multi-serie (si mapping lo indica)
                const fields = this.buildCardDataFields(queryResult, analysis.dataMapping, card.primaryColor);

                // Asignar colores si es necesario (sobre el data single-serie)
                const { processedData, updatedPrimaryColor } = this.assignColorsToCardData(updatedCard, fields.data || []);

                const finalCard = {
                  ...updatedCard,
                  ...fields,
                  data: processedData,
                  primaryColor: updatedPrimaryColor || card.primaryColor,
                  dataExpiresAt: Date.now() + CACHE_DURATION_MS, // Caché válido por 2 horas
                  error: undefined, // Limpiar error previo si existía
                };

                // Guardar card actualizado en localStorage para futuras ejecuciones
                this.updateCardMetadataInStorage(finalCard);

                return finalCard;
              })
            );
          } else {
            // El card ya tiene metadata reusable → SKIP llamada IA.
            // Solo re-ejecutamos el query y re-transformamos. Esto reduce llamadas al LLM
            // en refrescos sucesivos y respeta cualquier configuración manual previa
            // (ej: usuario ajustó label/series desde el diálogo).
            _log(`[executeCardQuery] Card ${card.id}: SKIP IA — reusando mapping existente`, {
              dataMapping: card.dataMapping,
              chartType: card.chartType,
            });

            // Transformar resultado: single-serie + multi-serie (si mapping lo indica)
            const fields = this.buildCardDataFields(queryResult, card.dataMapping, card.primaryColor);

            // Asignar colores si es necesario (sobre el data single-serie)
            const { processedData, updatedPrimaryColor } = this.assignColorsToCardData(card, fields.data || []);

            return of({
              ...card,
              ...fields,
              data: processedData,
              primaryColor: updatedPrimaryColor || card.primaryColor,
              dataExpiresAt: Date.now() + CACHE_DURATION_MS, // Caché válido por 2 horas
              error: undefined, // Limpiar error previo si existía
            });
          }
        }),
        catchError(error => {
          console.error(`[executeCardQuery] Error ejecutando query para card ${card.id}:`, error);

          // Capturar información adicional del error (como HTTP status)
          const errorDetails: any = {
            message: error.message || this.translate.instant('AI_DASHBOARD.ERRORS.EXECUTING_QUERY'),
            timestamp: new Date().toISOString(),
            sqlQuery: card.query, // Incluir el SQL query que falló
          };

          // Si el error viene de un HttpErrorResponse, capturar status y otros detalles
          if (error.status !== undefined) {
            errorDetails.httpStatus = error.status;
            errorDetails.httpStatusText = error.statusText;
          }
          if (error.url) {
            errorDetails.requestUrl = error.url;
          }
          if (error.method) {
            errorDetails.httpMethod = error.method;
          }
          if (error.relativePath) {
            errorDetails.relativePath = error.relativePath;
          }
          if (error.error) {
            errorDetails.serverError = error.error;
          }

          // NO BLOQUEAR: si el card tenía data buena, preservarla (isStale=true) en vez
          // de mostrar chart vacío. El usuario sigue viendo lo último que funcionó +
          // el indicador de error. Si nunca tuvo data, cae al comportamiento anterior.
          const errorMessage = error.message || this.translate.instant('AI_DASHBOARD.ERRORS.EXECUTING_QUERY');
          return of(this.buildStaleOrErrorCard(card, errorMessage, errorDetails));
        })
      );
    }

    // CASO 2: Card con data_fake → emular carga (para desarrollo/testing)
    _log(`[executeCardQuery] Card ${card.id} con data_fake - emulando carga`);

    const delay = 500 + Math.random() * 1000;
    return new Observable<DashboardCard>(observer => {
      setTimeout(() => {
        // Asignar colores a los datos fake
        const { processedData, updatedPrimaryColor } = this.assignColorsToCardData(card, card.data_fake || []);

        observer.next({
          ...card,
          data: processedData,
          primaryColor: updatedPrimaryColor || card.primaryColor,
          dataExpiresAt: Date.now() + CACHE_DURATION_MS, // Caché válido por 2 horas
          error: undefined, // Limpiar error previo si existía
          // Mantener data_fake para que se guarde en localStorage
        });
        observer.complete();
      }, delay);
    });
  }

  /**
   * Ejecuta queries de múltiples cards con estrategia mixta
   *
   * Estrategia híbrida:
   * - Cards con data_fake: PARALELO (emulación rápida, sin límites)
   * - Cards con queries reales (Databricks): SECUENCIAL (evitar rate limit "Too Many Requests")
   *
   * @param cards Array de cards con queries a ejecutar
   * @returns Observable que emite cada card individualmente cuando su data está lista
   */
  executeMultipleCardQueries(cards: DashboardCard[]): Observable<DashboardCard> {
    if (!cards || cards.length === 0) {
      _log('executeMultipleCardQueries - no cards to process');
      return new Observable(observer => observer.complete());
    }

    _log(`executeMultipleCardQueries - processing ${cards.length} cards`, cards);

    // Clasificar cards según tipo de ejecución
    // Cards con data estática O caché válido no necesitan re-ejecución
    // PERO: si tiene aiPrompt sin query, SÍ necesita procesarse (para generar query)
    const cardsWithStaticData = cards.filter(card => {
      const hasPromptOnly = !card.query && card.aiPrompt;
      if (false)
        _log(
          `[executeMultipleCardQueries] Card ${card.id}: hasPromptOnly=${hasPromptOnly}, query=${!!card.query}, aiPrompt=${!!card.aiPrompt}`
        );
      if (hasPromptOnly) {
        _log(`[executeMultipleCardQueries] Card ${card.id} tiene solo prompt, necesita generar query`);
        return false; // Necesita procesarse para generar query
      }
      return !card.query || this.isCacheValid(card);
    });

    // Cards que necesitan ejecutar query (sin caché válido)
    // O cards que tienen solo prompt (necesitan generar query primero)
    const cardsNeedingQuery = cards.filter(card => {
      const hasPromptOnly = !card.query && card.aiPrompt;
      if (false)
        _log(
          `[executeMultipleCardQueries] Card ${
            card.id
          } classification: hasQuery=${!!card.query}, hasPrompt=${!!card.aiPrompt}, hasPromptOnly=${hasPromptOnly}, cacheValid=${this.isCacheValid(
            card
          )}`
        );
      if (hasPromptOnly) {
        _log(`[executeMultipleCardQueries] → Card ${card.id} clasificado como "needs query generation"`);
        return true; // Necesita generar query desde prompt
      }

      const hasQuery = !!card.query;
      const needsExecution = !this.isCacheValid(card);
      return hasQuery && needsExecution;
    });

    // Separar entre cards con data_fake (paralelo) y sin data_fake (secuencial)
    const cardsWithFakeData = cardsNeedingQuery.filter(card => card.data_fake && card.data_fake.length > 0);
    const cardsWithRealQueries = cardsNeedingQuery.filter(card => !card.data_fake || card.data_fake.length === 0);

    _log(`[executeMultipleCardQueries] Clasificación:`, {
      estáticos: cardsWithStaticData.length,
      conDataFake: cardsWithFakeData.length,
      queriesReales: cardsWithRealQueries.length,
    });

    // Si no hay queries que ejecutar, solo devolver cards estáticos/cacheados
    if (cardsNeedingQuery.length === 0) {
      _log('executeMultipleCardQueries - todos los cards tienen data estática o caché válido');
      if (cardsWithStaticData.length === 0) {
        return new Observable(observer => observer.complete());
      }
      return merge(...cardsWithStaticData.map(card => of(card)));
    }

    // Crear observable que maneja ambas estrategias
    return new Observable<DashboardCard>(observer => {
      // Registrar todas las suscripciones para poder cancelarlas si el observer se desubscribe
      const internalSubs = new Subscription();

      // 1. Emitir cards estáticos/cacheados inmediatamente
      cardsWithStaticData.forEach(card => {
        observer.next(card);
        if (false) _log(`[executeMultipleCardQueries] Card ${card.id} emitido (${card.dataExpiresAt ? 'caché' : 'estático'})`);
      });

      // Control de finalización para cerrar el stream solo cuando AMBAS ramas terminen
      let fakeFinished = cardsWithFakeData.length === 0;
      let realFinished = cardsWithRealQueries.length === 0;

      const checkAllFinished = () => {
        if (fakeFinished && realFinished) {
          _log('[executeMultipleCardQueries] Todas las cards procesadas. Completando observer.');
          observer.complete();
        }
      };

      // 2. Ejecutar cards con data_fake EN PARALELO (rápido, sin rate limit)
      if (cardsWithFakeData.length > 0) {
        _log(`[executeMultipleCardQueries] Ejecutando ${cardsWithFakeData.length} cards con data_fake EN PARALELO`);

        const fakeDataObservables = cardsWithFakeData.map(card => this.executeCardQuery(card));

        const fakeSub = merge(...fakeDataObservables).subscribe({
          next: populatedCard => {
            observer.next(populatedCard);
            if (false) _log(`[executeMultipleCardQueries] Card con data_fake ${populatedCard.id} emitido`);
          },
          error: error => {
            console.error(`[executeMultipleCardQueries] Error en card con data_fake:`, error);
          },
          complete: () => {
            fakeFinished = true;
            checkAllFinished();
          },
        });
        internalSubs.add(fakeSub);
      }

      // 3. Ejecutar cards con queries reales SECUENCIALMENTE (evitar rate limit)
      if (cardsWithRealQueries.length > 0) {
        _log(`[executeMultipleCardQueries] Ejecutando ${cardsWithRealQueries.length} queries reales SECUENCIALMENTE (evitar rate limit)`);

        let currentIndex = 0;

        const executeNextRealQuery = () => {
          if (currentIndex >= cardsWithRealQueries.length) {
            // Todas las queries reales completadas
            realFinished = true;
            checkAllFinished();
            _log('[executeMultipleCardQueries] Rama de queries reales completada');
            return;
          }

          const card = cardsWithRealQueries[currentIndex];
          _log(
            `[executeMultipleCardQueries] Ejecutando query real ${currentIndex + 1}/${cardsWithRealQueries.length} para card ${card.id}`
          );

          const realSub = this.executeCardQuery(card).subscribe({
            next: populatedCard => {
              observer.next(populatedCard);
              if (false)
                _log(
                  `[executeMultipleCardQueries] Card con query real ${populatedCard.id} emitido (${currentIndex + 1}/${
                    cardsWithRealQueries.length
                  })`
                );

              currentIndex++;

              // Delay entre queries reales para respetar rate limit (500ms)
              setTimeout(() => executeNextRealQuery(), 500);
            },
            error: error => {
              console.error(`[executeMultipleCardQueries] Error en card ${card.id}:`, error);

              // Emitir card con error y continuar
              observer.next({
                ...card,
                data: [],
                description: `Error: ${error.message || this.translate.instant('AI_DASHBOARD.ERRORS.EXECUTING_QUERY')}`,
              });

              currentIndex++;
              setTimeout(() => executeNextRealQuery(), 500);
            },
          });
          internalSubs.add(realSub);
        };

        // Iniciar ejecución secuencial de queries reales
        executeNextRealQuery();
      } else {
        realFinished = true;
        checkAllFinished();
      }

      // Limpieza al desubscribirse
      return () => {
        internalSubs.unsubscribe();
      };
    });
  }

  /**
   * Carga cards y ejecuta sus queries automáticamente
   *
   * Combina loadCards() + executeMultipleCardQueries() en un solo flujo
   * Útil para inicialización del dashboard
   *
   * @returns Observable que emite cada card individualmente cuando está listo
   * @deprecated Usa loadCards() + executeMultipleCardQueries() separadamente para mayor control
   */
  loadCardsWithData(): Observable<DashboardCard> {
    return this.loadCards().pipe(switchMap(cards => this.executeMultipleCardQueries(cards)));
  }

  /**
   * Actualiza la metadata de un card específico en storage
   * Solo actualiza type, chartType, dataMapping y description del card
   * No afecta la posición ni otros cards
   *
   * @param updatedCard Card con metadata actualizada
   */
  private updateCardMetadataInStorage(updatedCard: DashboardCard): void {
    // Load all cards from storage
    this.storageProvider
      .loadCards()
      .pipe(
        switchMap(cards => {
          if (!cards || cards.length === 0) {
            _warn('[updateCardMetadataInStorage] No hay cards guardados en storage');
            return of(void 0);
          }

          const cardIndex = cards.findIndex(c => c.id === updatedCard.id);

          if (cardIndex === -1) {
            _warn(`[updateCardMetadataInStorage] Card ${updatedCard.id} no encontrado en storage`);
            return of(void 0);
          }

          // Actualizar metadata relevante, preservar posición y otras propiedades
          cards[cardIndex] = {
            ...cards[cardIndex],
            type: updatedCard.type,
            chartType: updatedCard.chartType,
            dataMapping: updatedCard.dataMapping,
            description: updatedCard.description,
            dataExpiresAt: updatedCard.dataExpiresAt,
            error: updatedCard.error,
          };

          // Guardar de nuevo
          return this.storageProvider
            .saveCards(cards)
            .pipe(tap(() => _log(`[updateCardMetadataInStorage] Metadata actualizada para card ${updatedCard.id}`)));
        }),
        catchError(error => {
          console.error('[updateCardMetadataInStorage] Error actualizando metadata:', error);
          return of(void 0);
        })
      )
      .subscribe();
  }

  /**
   * Refresca la data de un card específico re-ejecutando su query
   *
   * @param cardId ID del card a refrescar
   * @param allCards Array completo de cards actual
   * @returns Observable con el card actualizado
   */
  refreshCardData(cardId: string, allCards: DashboardCard[]): Observable<DashboardCard> {
    const card = allCards.find(c => c.id === cardId);

    if (!card) {
      _warn('Card no encontrado:', cardId);
      return of(null);
    }

    return this.executeCardQuery(card);
  }

  /**
   * Regenera el query SQL desde un prompt usando Databricks Genie AI,
   * analiza los datos con IA para obtener metadata (chartType, dataMapping),
   * y transforma los datos al formato del card
   *
   * IMPORTANTE: Siempre fuerza re-análisis con IA cuando hay datos disponibles,
   * incluso si el card tenía dataMapping previo, porque el nuevo query puede
   * tener columnas o estructura diferente al query anterior.
   *
   * @param aiPrompt Prompt en lenguaje natural
   * @param cardTitle Título del card para ayudar al análisis de IA (opcional)
   * @returns Observable con { query, data, analysis?, transformedData?, multiSeriesData?, useDualAxis? }
   */
  regenerateQueryFromPrompt(
    aiPrompt: string,
    cardTitle?: string
  ): Observable<{
    query: string | null;
    data: any[];
    analysis?: AICardAnalysis;
    transformedData?: DashboardCard['data'];
    multiSeriesData?: DashboardCard['multiSeriesData'];
    useDualAxis?: boolean;
    availableColumns?: string[];
  }> {
    if (!aiPrompt || !aiPrompt.trim()) {
      return throwError(() => new Error('El prompt no puede estar vacío'));
    }

    // Verificar si Databricks está disponible
    if (!this.dataBricksService.CAN_USE_DB_CHAT()) {
      _warn('[regenerateQueryFromPrompt] Datalake no está disponible');
      return throwError(() => new Error('Servicio de Datalake no disponible'));
    }

    _log('[regenerateQueryFromPrompt] Generando query desde prompt:', aiPrompt);

    // Usar queue para evitar rate limiting al generar queries desde prompts
    return this.queueDatabricksRequest(() => this.dataBricksService.sendGenieMessage(aiPrompt)).pipe(
      switchMap(response => {
        _log('[regenerateQueryFromPrompt] Respuesta de Lakehouse:', response);

        // Verificar si hay error
        if (response.error) {
          const errorMsg = response.text || `Lakehouse error: ${response.error}`;
          const enhancedError: any = new Error(errorMsg);

          // Preservar información HTTP si está disponible
          if (response.status !== undefined) {
            enhancedError.status = response.status;
            enhancedError.statusText = response.statusText;
            enhancedError.url = response.url;
          }

          throw enhancedError;
        }

        // Extraer query desde rawResponse.content (el SQL directo) o attachments
        let generatedQuery: string | null = null;

        // 1. Intentar desde content (cuando Genie genera SQL desde prompt)
        const content = response.rawResponse?.content;
        if (content && typeof content === 'string' && content.trim().toUpperCase().startsWith('SELECT')) {
          generatedQuery = content.trim();
          _log('[regenerateQueryFromPrompt] Query extraído desde content');
        } else if (content && typeof content === 'string' && content.trim().toUpperCase().startsWith('WITH')) {
          generatedQuery = content.trim();
          _log('[regenerateQueryFromPrompt] Query extraído desde content (CTE)');
        }

        // 2. Si no está en content, intentar desde attachments
        if (!generatedQuery) {
          const attachments = response.rawResponse?.attachments || [];
          const queryAttachment = attachments.find(att => att.query?.query);
          if (queryAttachment?.query?.query) {
            generatedQuery = queryAttachment.query.query;
            _log('[regenerateQueryFromPrompt] Query extraído desde attachments');
          }
        }

        if (!generatedQuery) {
          _warn('[regenerateQueryFromPrompt] No se recibió query en la respuesta');
          _warn('[regenerateQueryFromPrompt] Content:', content);
          _warn('[regenerateQueryFromPrompt] Attachments:', response.rawResponse?.attachments);
          // NO lanzar error - permitir que el componente maneje este caso
          // Retornar null para indicar que no hay nuevo query (se mantendrá el existente)
          _warn('[regenerateQueryFromPrompt] Datalake respondió sin generar query SQL - se mantendrá el query existente');
        }

        // Extraer datos del queryProcessResult (Genie ya ejecutó la query al generar el SQL)
        let queryData: any[] = [];

        if (response.queryProcessResult) {
          // queryProcessResult es un GenieStatementResponse que necesita transformación
          queryData = this.transformGenieResponseToRows(response.queryProcessResult);
          _log(`[regenerateQueryFromPrompt] Datos extraídos y transformados de queryProcessResult (${queryData.length} filas)`);
        } else {
          _warn('[regenerateQueryFromPrompt] No hay queryProcessResult en la respuesta - los datos deberán obtenerse por re-ejecución');
        }

        if (generatedQuery) {
          _log('[regenerateQueryFromPrompt] Query generado exitosamente:', generatedQuery);
        }

        // Si hay datos, analizarlos con IA para obtener metadata
        if (queryData.length > 0) {
          _log('[regenerateQueryFromPrompt] Analizando datos con IA...');
          return this.analyzeQueryResultsWithAI(queryData, cardTitle, aiPrompt).pipe(
            map(analysis => {
              _log('[regenerateQueryFromPrompt] Análisis de IA completado:', analysis);

              // Transformar datos usando el dataMapping generado (single + multi cuando aplica)
              const fields = this.buildCardDataFields(queryData, analysis.dataMapping);
              _log(`[regenerateQueryFromPrompt] Datos transformados (${fields.data?.length ?? 0} registros)`);

              return {
                query: generatedQuery || null,
                data: queryData,
                analysis,
                transformedData: fields.data,
                multiSeriesData: fields.multiSeriesData,
                useDualAxis: fields.useDualAxis,
                availableColumns: fields.availableColumns,
              };
            }),
            catchError(aiError => {
              console.error('[regenerateQueryFromPrompt] Error en análisis de IA:', aiError);
              // Fallback: retornar sin análisis
              return of({
                query: generatedQuery || null,
                data: queryData,
                analysis: undefined,
                transformedData: undefined,
                multiSeriesData: undefined,
                useDualAxis: undefined,
                availableColumns: undefined,
              });
            })
          );
        } else {
          // Sin datos, retornar solo el query
          return of({
            query: generatedQuery || null,
            data: queryData,
            analysis: undefined,
            transformedData: undefined,
          });
        }
      }),
      catchError(error => {
        console.error('[regenerateQueryFromPrompt] Error generando query:', error);
        throw error;
      })
    );
  }

  // ============================================================================
  // DASHBOARD GALLERIES (TABS) MANAGEMENT
  // ============================================================================

  /**
   * Save galleries to storage
   *
   * Current: localStorage
   * Future: POST/PUT /api/dashboard/galleries
   *
   * TODO: For API implementation, replace of() with HTTP call:
   * return this.http.put<void>('/api/dashboard/galleries', galleries);
   */
  /**
   * Save galleries to storage
   *
   * Uses configured storage provider (localStorage or API)
   * Automatically adds 'user' property if not present
   */
  saveGalleries(galleries: DashboardGallery[]): Observable<void> {
    const galleriesWithUser = this.ensureUserInGalleries(galleries);
    return this.storageProvider.saveGalleries(galleriesWithUser);
  }

  /**
   * Load galleries from storage
   * Returns default galleries if no saved data exists
   *
   * Uses configured storage provider (localStorage or API)
   */
  loadGalleries(): Observable<DashboardGallery[]> {
    return this.storageProvider.loadGalleries().pipe(
      switchMap(loadedGalleries => {
        if (loadedGalleries && loadedGalleries.length > 0) {
          _log('Galleries loaded from storage provider');
          return of(loadedGalleries);
        }
        // No saved galleries, return default galleries
        _log('No saved galleries found, returning default galleries');
        return this.getDefaultGalleries();
      }),
      catchError(error => {
        console.error('Error loading dashboard galleries:', error);
        // On error, return default galleries
        return this.getDefaultGalleries();
      })
    );
  }

  /**
   * Get default galleries configuration
   */
  getDefaultGalleries(): Observable<DashboardGallery[]> {
    const galleries = (_isDev() ? _defaultGalleries_DEV : _defaultGalleries_PROD) as DashboardGallery[];
    _log(
      '[getDefaultGalleries] Returning galleries:',
      galleries.map(g => ({ id: g.id, name: g.name }))
    );
    return of(galleries);
  }

  /**
   * Create a new gallery with default configuration
   */
  createNewGallery(): Observable<DashboardGallery> {
    return of({
      id: `gallery-${Date.now()}`,
      name: this.translate.instant('AI_DASHBOARD.GALLERIES.NEW_GALLERY'),
    });
  }

  /**
   * Get cards filtered by gallery ID
   * @param galleryId ID of the gallery to filter by (undefined returns cards without gallery)
   * @param allCards Array of all cards
   */
  getCardsByGallery(galleryId: string | undefined, allCards: DashboardCard[]): DashboardCard[] {
    if (galleryId === undefined) {
      // Return cards that don't belong to any gallery
      return allCards.filter(card => !card.galleryId);
    }
    return allCards.filter(card => card.galleryId === galleryId);
  }

  // ============================================================================
  // CARD OPERATIONS (EXISTING)
  // ============================================================================
}

const _defaultGalleries_PROD: DashboardGallery[] = [
  {
    id: 'gallery-default',
    name: 'Principal',
    isDeleted: false,
  },
];

const _defaultGalleries_DEV: DashboardGallery[] = [
  {
    id: 'gallery-default',
    name: 'Principal',
    isDeleted: false,
  },
  {
    id: 'gallery-ventas',
    name: 'Ventas y Revenue',
    isDeleted: false,
  },
  {
    id: 'gallery-inventario',
    name: 'Inventario y Stock',
    isDeleted: false,
  },
];

const _defaultDataCards_PROD: DashboardCard[] = [
  {
    id: 'ventas-ultimos-6-meses',
    title: 'Ventas Últimos 6 Meses',
    aiPrompt: 'Muestra las ventas por mes totales de los últimos 6 Meses.',
    x: 0,
    y: 0,
    w: 3,
    h: 1,
  },
];

const _defaultDataCards_DEV: DashboardCard[] = [
  //   Row 0: 3 KPI Cards (queries reales)
  {
    id: 'ventas-ultimos-6-meses',
    title: 'Ventas Últimos 6 Meses',
    aiPrompt: 'Ventas Últimos 6 Meses',
    x: 0,
    y: 0,
    w: 1,
    h: 1,
  },
  {
    id: 'total-ventas-6m-kpi',
    title: 'Total Ventas Últimos 5 Meses',
    // query:
    //   'WITH last_6_periods AS (SELECT DISTINCT year, month FROM `prd`.`prismart`.`sales_sales_per_month` WHERE year IS NOT NULL AND month IS NOT NULL ORDER BY year DESC, month DESC LIMIT 6) SELECT "Total Ventas 6M" as metric_name, SUM(sales_total) AS total_sales FROM `prd`.`prismart`.`sales_sales_per_month` WHERE (year, month) IN (SELECT year, month FROM last_6_periods) AND sales_total IS NOT NULL',
    aiPrompt: 'Total de ventas acumulado de los últimos 5 meses',
    x: 1,
    y: 0,
    w: 1,
    h: 1,
  },
  {
    id: 'conversion-kpi',
    title: 'Rotación de Stock',
    type: 'kpi',
    aiPrompt: 'Muéstrame la rotación de inventario de mi categoría comparada con el mes anterior',
    x: 2,
    y: 0,
    w: 1,
    h: 1,
    data_fake: [
      {
        label: 'Índice de Rotación',
        value: 6.8,
        unit: 'x',
        status: 'success',
        previousValue: 5.9,
        trend: 'up',
        color: '#4CAF50',
      },
    ],
  },
  // Row 1: Ranking marcas (query real) + Top Subcategorías (2 cols)
  {
    id: 'ranking-marcas',
    title: 'Ranking marcas más vendidas',
    aiPrompt: 'ranking de las 5 marcas más vendidas',
    x: 0,
    y: 1,
    w: 1,
    h: 1,
  },
  {
    id: 'sales-performance',
    title: 'Top Subcategorías por Ventas',
    type: 'chart',
    chartType: 'bar',
    primaryColor: null,
    query:
      'SELECT subcategory_name, (SUM(sales_amount) * 100.0 / (SELECT SUM(sales_amount) FROM sales WHERE category = current_category)) as percentage FROM sales WHERE category = current_category GROUP BY subcategory_name ORDER BY percentage DESC',
    aiPrompt: 'Muestra qué subcategorías están generando más ventas dentro de mi categoría',
    x: 1,
    y: 1,
    w: 2,
    h: 1,
    data_fake: [
      { label: 'Lácteos Frescos', value: 42, unit: '%', color: '#4CAF50' },
      { label: 'Yogures', value: 28, unit: '%', color: '#2196F3' },
      { label: 'Quesos', value: 18, unit: '%', color: '#FF9800' },
      { label: 'Mantequilla', value: 8, unit: '%', color: '#9C27B0' },
      { label: 'Postres', value: 4, unit: '%', color: '#607D8B' },
    ],
  },
  // Row 2: Salud del Surtido (2 cols) + Stock Crítico
  {
    id: 'inventory-metrics',
    title: 'Salud del Surtido',
    type: 'chart',
    chartType: 'bar',
    primaryColor: null,
    query:
      'SELECT assortment_health_metric, (COUNT(*) * 100.0 / (SELECT COUNT(*) FROM products WHERE category = current_category)) as percentage FROM products WHERE category = current_category GROUP BY assortment_status',
    aiPrompt: 'Analiza la salud del surtido de mi categoría: SKUs activos, descontinuados, nuevos lanzamientos',
    x: 0,
    y: 2,
    w: 2,
    h: 1,
    data_fake: [
      { label: 'SKUs Activos', value: 82, unit: '%', color: '#4CAF50' },
      { label: 'Alto Desempeño', value: 28, unit: '%', color: '#2196F3' },
      { label: 'Bajo Desempeño', value: 15, unit: '%', color: '#F44336' },
      { label: 'Nuevos Lanzamientos', value: 8, unit: '%', color: '#FF9800' },
      { label: 'Descontinuados', value: 6, unit: '%', color: '#9C27B0' },
      { label: 'En Evaluación', value: 12, unit: '%', color: '#607D8B' },
    ],
  },
  {
    id: 'store-status',
    title: 'Stock Crítico por Tienda',
    type: 'status',
    query:
      'SELECT store_name, (current_stock * 100.0 / optimal_stock) as stock_level, CASE WHEN stock_level >= 80 THEN "active" ELSE "inactive" END as status FROM inventory WHERE category = current_category AND date = CURRENT_DATE()',
    aiPrompt: 'Muéstrame qué tiendas tienen niveles críticos de inventario en mi categoría',
    x: 2,
    y: 2,
    w: 1,
    h: 1,
    data_fake: [
      { label: 'Sucursal Centro', value: 95, status: 'active', unit: '%' },
      { label: 'Mall Norte', value: 87, status: 'active', unit: '%' },
      { label: 'Plaza Sur', value: 45, status: 'inactive', unit: '%' },
    ],
  },
  // Row 3: Tendencia de Ventas (full width)
  {
    id: 'monthly-sales',
    title: 'Tendencia de Ventas de Bebidas',
    type: 'chart',
    chartType: 'line',
    primaryColor: null,
    query:
      'SELECT DATE_FORMAT(sale_date, "%b") as month, SUM(total_amount) as total_sales FROM sales WHERE category = current_category AND YEAR(sale_date) = 2024 GROUP BY MONTH(sale_date) ORDER BY sale_date',
    aiPrompt: 'Muestra la evolución de las ventas de mi categoría mes a mes durante el año 2024',
    x: 0,
    y: 3,
    w: 3,
    h: 1,
    data_fake: [
      { label: 'Ene', value: 1250000, unit: 'COP' },
      { label: 'Feb', value: 1180000, unit: 'COP' },
      { label: 'Mar', value: 1420000, unit: 'COP' },
      { label: 'Abr', value: 1350000, unit: 'COP' },
      { label: 'May', value: 1580000, unit: 'COP' },
      { label: 'Jun', value: 1680000, unit: 'COP' },
      { label: 'Jul', value: 1720000, unit: 'COP' },
      { label: 'Ago', value: 1650000, unit: 'COP' },
      { label: 'Sep', value: 1890000, unit: 'COP' },
      { label: 'Oct', value: 2100000, unit: 'COP' },
      { label: 'Nov', value: 2450000, unit: 'COP' },
    ],
  },
  // Row 4: Desempeño por Formato (2 cols) + Participación de Marcas
  {
    id: 'regional-distribution',
    title: 'Desempeño por Formato de Tienda',
    type: 'chart',
    chartType: 'horizontal',
    primaryColor: null,
    query:
      'SELECT store_format, (SUM(sales_amount) * 100.0 / (SELECT SUM(sales_amount) FROM sales WHERE category = current_category)) as percentage FROM sales s JOIN stores st ON s.store_id = st.id WHERE category = current_category GROUP BY st.store_format ORDER BY percentage DESC',
    aiPrompt: 'Compara el desempeño de mi categoría según el formato de tienda: hipermercado, supermercado, express',
    x: 0,
    y: 4,
    w: 2,
    h: 1,
    data_fake: [
      { label: 'Hipermercados', value: 45, unit: '%', color: '#1976D2' },
      { label: 'Supermercados', value: 32, unit: '%', color: '#4CAF50' },
      { label: 'Tiendas Express', value: 15, unit: '%', color: '#FF9800' },
      { label: 'Tiendas de Barrio', value: 8, unit: '%', color: '#9C27B0' },
    ],
  },
  {
    id: 'payment-methods',
    title: 'Participación de Marcas',
    type: 'chart',
    chartType: 'pie',
    primaryColor: null,
    query:
      'SELECT brand_name, (SUM(sales_amount) * 100.0 / (SELECT SUM(sales_amount) FROM sales WHERE category = current_category)) as percentage FROM sales WHERE category = current_category GROUP BY brand_name ORDER BY percentage DESC',
    aiPrompt: 'Muestra la participación de mercado de las principales marcas en mi categoría',
    x: 2,
    y: 4,
    w: 1,
    h: 1,
    data_fake: [
      { label: 'Marca Líder', value: 38, unit: '%', color: '#2196F3' },
      { label: 'Marca Premium', value: 24, unit: '%', color: '#4CAF50' },
      { label: 'Marca Económica', value: 18, unit: '%', color: '#FF9800' },
      { label: 'Marca Propia', value: 12, unit: '%', color: '#9C27B0' },
      { label: 'Otras Marcas', value: 8, unit: '%', color: '#607D8B' },
    ],
    insights: ['↗ 5% marca propia', '↓ 3% marca líder'],
  },
  // Row 5: Indicadores de Categoría (full width)
  {
    id: 'key-metrics',
    title: 'Indicadores de Categoría',
    type: 'list',
    query:
      'SELECT COUNT(DISTINCT sku) as active_skus, AVG(price_per_unit) as avg_price, SUM(units_sold) as total_units, (SUM(margin_amount) * 100.0 / SUM(sales_amount)) as margin_pct FROM category_metrics WHERE category = current_category AND month = CURRENT_MONTH()',
    aiPrompt: 'Resumen de los indicadores clave de mi categoría: SKUs activos, precio promedio, unidades vendidas y margen',
    x: 0,
    y: 5,
    w: 3,
    h: 1,
    data_fake: [
      { label: 'SKUs Activos', value: 248, unit: '' },
      { label: 'Precio Promedio', value: 8540, unit: 'COP' },
      { label: 'Unidades Vendidas', value: 45680, unit: '' },
      { label: 'Margen Categoría', value: 32, unit: '%' },
    ],
  },
];
